import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import AddRoadIcon from '@mui/icons-material/AddRoad';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import UndoIcon from '@mui/icons-material/Undo';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { alpha, useTheme } from '@mui/material/styles';
import { Marker, Source, Layer, type MapRef } from '@vis.gl/react-maplibre';
import type { ExpressionSpecification, MapMouseEvent } from 'maplibre-gl';
import MapView from 'components/Map/MapView';
import {
  applyNeutralBackdrop,
  boundsToMaxBoundsTuple,
  geoJsonCentroid,
  getPaddedLonLatBounds,
  NEUTRAL_ROAD_MAX_ZOOM,
  setBasemap,
  type BasemapId,
  type LocalGeoJsonFeatureCollection,
} from 'utils/maplibre';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';
import { useMapFlyTo } from 'hooks/useMapFlyTo';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import {
  chaikinSmooth,
  decodeGeohashBounds,
  encodeGeohash,
  geoPointsToPathGeog,
  geoPointsToZoneGeog,
  geohashUnionRings,
  pointInGeoRing,
  zoneGeogToGeoPoints,
  type GeoPoint,
} from 'utils/geohash';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { selectLatestTagData } from 'store/device.slice';
import { latLonToUtm } from 'utils/utmToLatLon';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import HaulRouteAPI, {
  type HaulRoute,
  type RouteDistancePathOption,
} from 'api/haul-route.api';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';
import GradeImporterAPI from 'api/grade-importer.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import {
  ALMALY_ROADS_COLOR,
  BLOCKED_ROUTE_COLOR,
  CAR_CUSTOMER_CODE,
  CENTROID_PIN_ICON_PX,
  DEFAULT_GEOHASH_PRECISION,
  DRAG_HIT_PX,
  EMPTY_NEW_LOCATION,
  GRADE_CATEGORY_COLORS,
  GRADE_ZONE_COLOR,
  INACTIVE_ROUTE_COLOR,
  PENDING_ROUTE_COLOR,
  ROAD_DESTINATION_HIT_RADIUS_METERS,
  ROAD_DISPLAY_WIDTH_METERS,
  ROUTE_COLOR,
  FOUND_ROUTE_COLOR,
  IMPORTED_GEOJSON_COLOR,
  type BlockedRoadEntry,
  type NewLocationDraft,
  type PendingRoute,
} from './RoadsImporter/roadsImporter.constants';
import {
  buildCircleGeoPoints,
  buildRouteOptionPath,
  distToSegmentMeters,
  findOverlappingRoutes,
  findReverseRoute,
  formatDistance,
  formatRoadName,
  gradeCategoryColorHex,
  groupSegmentsByWidth,
  haversineMeters,
  locationCenterPoint,
  locationPinIconDataUri,
  locationRing,
  pathDistanceMeters,
  ringAreaApprox,
  reconstructRoutePath,
  reverseRoadWidths,
  zoneColorHex,
} from './RoadsImporter/roadsImporter.helpers';
import { useTruckMarkers } from './RoadsImporter/useTruckMarkers';
import { useMineMapOverlay } from './RoadsImporter/useMineMapOverlay';
import {
  RoadsImporterLegend,
  type LegendItem,
} from './RoadsImporter/RoadsImporterLegend';
import {
  RoadsImporterContextMenu,
  type ContextMenuState,
} from './RoadsImporter/RoadsImporterContextMenu';
import { PendingRoutesPopover } from './RoadsImporter/PendingRoutesPopover';
import { FindRoutePanel } from './RoadsImporter/FindRoutePanel';
import {
  GradeDetailsPopover,
  type GradeDetailsState,
} from './RoadsImporter/GradeDetailsPopover';
import { MapGradeBlocksDialog } from './RoadsImporter/MapGradeBlocksDialog';
import { UnmapGradeBlocksDialog } from './RoadsImporter/UnmapGradeBlocksDialog';
import { AddLocationDialog } from './RoadsImporter/AddLocationDialog';
import { DeleteNodeDialog } from './RoadsImporter/DeleteNodeDialog';
import { DeleteRouteDialog } from './RoadsImporter/DeleteRouteDialog';
import { ViewRouteDialog } from './RoadsImporter/ViewRouteDialog';
import { EditRouteDialog } from './RoadsImporter/EditRouteDialog';
import { RoadWidthDialog } from './RoadsImporter/RoadWidthDialog';
import { SaveRoadDialog } from './RoadsImporter/SaveRoadDialog';
import { RoadsImporterFaqDialog } from './RoadsImporter/RoadsImporterFaqDialog';
import {
  BlockRoadDialog,
  type BlockRoadFormState,
  type BlockRoadSubmission,
} from './RoadsImporter/BlockRoadDialog';
import { UnblockRoadDialog } from './RoadsImporter/UnblockRoadDialog';
import {
  RoadHoverTooltip,
  type RoadHoverEntry,
  type RoadHoverState,
} from './RoadsImporter/RoadHoverTooltip';

const ROUTE_HOVER_THROTTLE_MS = 70;

// 'draw' — left-click adds a boundary point (default).
// 'vertex' — left-click-drag moves one existing vertex.
// 'shape' — left-click-drag inside the polygon translates the whole thing.
type BoundaryEditMode = 'draw' | 'vertex' | 'shape';

// Replaces the Cesium corridor's per-frame `zoomAwareRoadWidth(cameraHeight)`
// CallbackProperty (which grew the corridor's *real-world* width past a
// camera-height threshold so it never shrank to a sub-pixel sliver when
// zoomed out) with a native MapLibre zoom expression. MapLibre's line-width
// is pixel-space, not real-world meters, so this isn't a literal port of the
// formula — it's the idiomatic MapLibre equivalent of the same goal (roads
// read as a reasonable, visible ribbon at any zoom, without a JS callback
// re-run on every camera move): thin and unobtrusive zoomed out, thicker
// zoomed in close. ROAD_DISPLAY_WIDTH_METERS is already documented as a
// fixed *display* width unrelated to any road's real stored width, so this
// preserves that same "uniform decorative width" behavior.
// Caps how far in a user can zoom once the camera has landed on the site —
// matches the ceiling used by other editing-oriented maps in the app (see
// MAX_ZOOM in LocationBoundaryDrawer.tsx).
const MINE_MAP_MAX_ZOOM = 19;

// The default neutral-road view sidesteps the "Map data not yet available"
// Esri placeholder entirely via applyNeutralBackdrop (no raster tiles
// requested at all), but satellite mode needs real Esri imagery tiles to be
// useful, and real satellite coverage for these sites runs out well before
// the raster source's own maxzoom (19) — zooming past it shows the
// placeholder. Capping satellite's interactive zoom to the same ceiling the
// neutral-road basemap uses (see NEUTRAL_ROAD_MAX_ZOOM in utils/maplibre.ts,
// and the matching fix in FleetOverview's AssetMap) keeps users from zooming
// in far enough to reach it.
const SATELLITE_TOGGLE_MAX_ZOOM = NEUTRAL_ROAD_MAX_ZOOM;

const ROAD_LINE_WIDTH_EXPRESSION: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  10,
  1.5,
  14,
  3,
  17,
  5,
  20,
  8,
];

// A user-imported GeoJSON file can mix any geometry type, so the overlay
// renders it through three layers, each filtered to the geometry types it
// knows how to draw — fill for area geometries, line for both linear
// geometries and polygon outlines, circle for point geometries.
const IMPORTED_GEOJSON_POLYGON_FILTER: ExpressionSpecification = [
  'any',
  ['==', ['geometry-type'], 'Polygon'],
  ['==', ['geometry-type'], 'MultiPolygon'],
];
const IMPORTED_GEOJSON_LINE_FILTER: ExpressionSpecification = [
  'any',
  ['==', ['geometry-type'], 'LineString'],
  ['==', ['geometry-type'], 'MultiLineString'],
  ['==', ['geometry-type'], 'Polygon'],
  ['==', ['geometry-type'], 'MultiPolygon'],
];
const IMPORTED_GEOJSON_POINT_FILTER: ExpressionSpecification = [
  'any',
  ['==', ['geometry-type'], 'Point'],
  ['==', ['geometry-type'], 'MultiPoint'],
];

// GeoJSON Polygon rings must be explicitly closed (first point repeated as
// the last) — Cesium's PolygonHierarchy/polyline positions had no such
// requirement, so every ring built from a GeoPoint[] needs this on the way
// into a Feature's coordinates.
const closeRing = (ring: GeoPoint[]): number[][] => {
  const coords = ring.map((p) => [p.lon, p.lat]);
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    coords.push(first);
  }
  return coords;
};

// Small dark chip-style label used for road/route distance text — replaces
// Cesium's LabelGraphics (showBackground + outline) with a plain MUI Box
// rendered inside a <Marker>, matching the label pattern already used by
// DispatchMap.tsx and FleetOverview's AssetMap elsewhere in this migration.
// pointerEvents: 'none' is required here — every interaction in this view
// (add-vertex clicks, vertex drag hit-testing, right-click menus) is driven
// entirely by the shared map-level event handlers below via manual pixel
// hit-testing, never by a Marker's own DOM node, so labels/markers must let
// clicks fall through to the map underneath them.
const RoadDistanceLabel = ({
  color,
  fontSize = 10,
  children,
}: {
  color: string;
  fontSize?: number;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      px: '6px',
      py: '2px',
      borderRadius: '4px',
      bgcolor: alpha(color, 0.75),
      color: '#fff',
      fontWeight: 700,
      fontSize,
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0,0,0,0.85)',
      pointerEvents: 'none',
    }}
  >
    {children}
  </Box>
);

// Location name label — same idea as RoadDistanceLabel, styled to match the
// old dark-background/white-text Cesium label (dimmed to gray text for
// inactive locations).
const LocationNameLabel = ({
  dimmed,
  children,
}: {
  dimmed: boolean;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      px: '6px',
      py: '2px',
      borderRadius: '4px',
      bgcolor: 'rgba(0,0,0,0.55)',
      color: dimmed ? '#d3d3d3' : '#fff',
      fontWeight: 700,
      fontSize: 11,
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0,0,0,0.9)',
      pointerEvents: 'none',
    }}
  >
    {children}
  </Box>
);

const RoadsImporterContent = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const locationApi = useRef(new LocationImporterAPI()).current;
  const haulRouteApi = useRef(new HaulRouteAPI()).current;
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const gradeApi = useRef(new GradeImporterAPI()).current;

  const mapRef = useRef<MapRef | null>(null);
  const { flyTo, fitBounds } = useMapFlyTo(mapRef);
  const hasCenteredRef = useRef(false);
  // Ground point of the last right-click — used to seed a new Intersection
  // location's auto-circle boundary at the spot the user actually clicked.
  const rightClickPointRef = useRef<GeoPoint | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [locations, setLocations] = useState<ImportedLocation[]>([]);
  const [routes, setRoutes] = useState<HaulRoute[]>([]);
  // Server-authoritative blocked state, keyed by roadId (== routeId) —
  // refreshed on a poll since roads can auto-unblock server-side (TTL
  // expiry) with no client-visible event.
  const [blockedRoads, setBlockedRoads] = useState<
    Map<string, BlockedRoadEntry>
  >(new Map());
  const [blockRouteTarget, setBlockRouteTarget] = useState<HaulRoute | null>(
    null
  );
  const [blockRouteForm, setBlockRouteForm] = useState<BlockRoadFormState>({
    reason: '',
    ttl: { hours: '', minutes: '' },
  });
  const [blockingRoute, setBlockingRoute] = useState(false);
  const [unblockRouteTarget, setUnblockRouteTarget] =
    useState<HaulRoute | null>(null);
  const [unblockingRoute, setUnblockingRoute] = useState(false);
  const [grades, setGrades] = useState<GradeRecord[]>([]);
  const [showGrades, setShowGrades] = useState(false);
  const [gradeDetailsTarget, setGradeDetailsTarget] =
    useState<GradeDetailsState | null>(null);
  const [mapGradeBlocksTarget, setMapGradeBlocksTarget] =
    useState<ImportedLocation | null>(null);
  const [unmapConfirmOpen, setUnmapConfirmOpen] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [hideRouteDistances, setHideRouteDistances] = useState(false);
  const [routeFilterLocation, setRouteFilterLocation] =
    useState<ImportedLocation | null>(null);
  const [showRoadsOverlay, setShowRoadsOverlay] = useState(true);
  const [showAssets, setShowAssets] = useState(false);

  // "Find Route" panel — from/to/via directions search over the haul-route
  // paths API, Google-Maps-style. findRouteResults stays null until a search
  // actually runs (distinct from an empty array, which means "searched, no
  // route exists") so the results section only renders after a search.
  const [findRouteOpen, setFindRouteOpen] = useState(false);
  const [findRouteFrom, setFindRouteFrom] = useState<ImportedLocation | null>(
    null
  );
  const [findRouteTo, setFindRouteTo] = useState<ImportedLocation | null>(null);
  const [findRouteVia, setFindRouteVia] = useState<ImportedLocation[]>([]);
  const [findRouteLoading, setFindRouteLoading] = useState(false);
  const [findRouteResults, setFindRouteResults] = useState<
    RouteDistancePathOption[] | null
  >(null);
  const [findRouteSelectedIndex, setFindRouteSelectedIndex] = useState<
    number | null
  >(null);

  const [editingLocation, setEditingLocation] =
    useState<ImportedLocation | null>(null);
  const [draftVertices, setDraftVertices] = useState<GeoPoint[]>([]);
  const [boundaryEditMode, setBoundaryEditMode] =
    useState<BoundaryEditMode>('draw');
  // Live lat/lon offset while dragging a whole shape — lets connected
  // routes' rendered lines follow the shape in real time, before Save.
  const [shapeDragDelta, setShapeDragDelta] = useState<GeoPoint | null>(null);
  const [isDraggingVertex, setIsDraggingVertex] = useState(false);
  const [saving, setSaving] = useState(false);
  // "Move Centroid" mode: isolates the map to one location so its label/
  // road-destination anchor point can be dragged independently of its
  // polygon. Separate from editingLocation (boundary reshape) — the two
  // never run at once.
  const [movingCentroidLocation, setMovingCentroidLocation] =
    useState<ImportedLocation | null>(null);
  const [centroidDraft, setCentroidDraft] = useState<GeoPoint | null>(null);
  const [savingCentroid, setSavingCentroid] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [hoveredRoutesTarget, setHoveredRoutesTarget] =
    useState<RoadHoverState | null>(null);
  const lastRouteHoverAtRef = useRef(0);
  const hoveredRouteNamesKeyRef = useRef<string | null>(null);
  const [widthEditTarget, setWidthEditTarget] = useState<number | null>(null);
  const [widthEditValue, setWidthEditValue] = useState('50');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ImportedLocation | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);
  const [deleteRouteTarget, setDeleteRouteTarget] = useState<HaulRoute | null>(
    null
  );
  const [deletingRoute, setDeletingRoute] = useState(false);
  const [viewRouteTarget, setViewRouteTarget] = useState<HaulRoute | null>(
    null
  );
  const [editRouteTarget, setEditRouteTarget] = useState<HaulRoute | null>(
    null
  );
  const [editRouteRoadName, setEditRouteRoadName] = useState('');
  const [editRouteMinutes, setEditRouteMinutes] = useState('5');
  const [editRouteCapacity, setEditRouteCapacity] = useState('3');
  const [editRouteWeightCapacity, setEditRouteWeightCapacity] = useState('');
  const [editRouteSpeedLimitMin, setEditRouteSpeedLimitMin] = useState('40');
  const [editRouteSpeedLimitMax, setEditRouteSpeedLimitMax] = useState('40');
  const [editRouteLanes, setEditRouteLanes] = useState('1');
  const [editRouteCondition, setEditRouteCondition] = useState('good');
  const [editRouteSuggestions, setEditRouteSuggestions] = useState('');
  const [savingRouteEdit, setSavingRouteEdit] = useState(false);
  // Route whose linestring is being reshaped — interior joints only
  // (endpoints stay pinned to their locations), via the shared vertex-drag
  // mechanics used for location boundary editing.
  const [editingRouteTarget, setEditingRouteTarget] =
    useState<HaulRoute | null>(null);
  // Which location each end of the route being path-edited currently
  // resolves to — seeded from the route's fromLocationId/toLocationId, and
  // reassigned live when an endpoint is dragged onto a different location.
  const [editingRouteFrom, setEditingRouteFrom] =
    useState<ImportedLocation | null>(null);
  const [editingRouteTo, setEditingRouteTo] = useState<ImportedLocation | null>(
    null
  );
  const [newLocation, setNewLocation] =
    useState<NewLocationDraft>(EMPTY_NEW_LOCATION);
  const [creatingLocation, setCreatingLocation] =
    useState<NewLocationDraft | null>(null);
  const [drawingRoadFrom, setDrawingRoadFrom] =
    useState<ImportedLocation | null>(null);
  const [roadTo, setRoadTo] = useState<ImportedLocation | null>(null);
  const [roadWidth, setRoadWidth] = useState('10');
  const [roadRoadName, setRoadRoadName] = useState('');
  const [roadReturnRoadName, setRoadReturnRoadName] = useState('');
  const [roadSameNameBothWays, setRoadSameNameBothWays] = useState(false);
  const [roadMinutes, setRoadMinutes] = useState('5');
  const [roadCapacity, setRoadCapacity] = useState('3');
  const [roadWeightCapacity, setRoadWeightCapacity] = useState('');
  const [roadSpeedLimitMin, setRoadSpeedLimitMin] = useState('40');
  const [roadSpeedLimitMax, setRoadSpeedLimitMax] = useState('40');
  const [roadLanes, setRoadLanes] = useState('1');
  const [roadCondition, setRoadCondition] = useState('good');
  const [roadSuggestions, setRoadSuggestions] = useState('');
  const [roadTwoWay, setRoadTwoWay] = useState(true);
  // Roads drawn this session queue here until "Save All Routes" fires —
  // each pending route becomes exactly one POST to the haul-route API.
  const [pendingRoutes, setPendingRoutes] = useState<PendingRoute[]>([]);
  const [roadVertexWidths, setRoadVertexWidths] = useState<number[]>([]);
  const [savingAllRoutes, setSavingAllRoutes] = useState(false);
  const [pendingListAnchor, setPendingListAnchor] =
    useState<HTMLElement | null>(null);
  // Satellite/neutral-road basemap toggle, available to every customer —
  // defaults to the neutral-road (natural road map) view. This is a UI-level
  // choice only: the "neutralRoad" mode itself is implemented as the flat
  // neutral backdrop (applyNeutralBackdrop, no raster tiles requested at
  // all), not live Esri "Canvas/World_Light_Gray" tiles — matching every
  // other map in the app (FleetOverview's AssetMap, Road Condition's
  // PatchMap, utils/siteRoadOverlay.ts) which all avoid requesting that
  // layer's tiles directly for exactly this reason: it has real per-site
  // coverage gaps and returns its "no data yet" placeholder as a normal
  // 200 response, indistinguishable from real data without inspecting the
  // response body. This view's own location/road/grade GeoJSON layers
  // already carry the visual detail on top of the flat backdrop.
  const [mapBasemap, setMapBasemap] = useState<BasemapId>('neutralRoad');
  const [importedGeoJson, setImportedGeoJson] =
    useState<LocalGeoJsonFeatureCollection | null>(null);
  const [importedGeoJsonName, setImportedGeoJsonName] = useState<string | null>(
    null
  );
  const geoJsonFileInputRef = useRef<HTMLInputElement>(null);

  const editPrecisionRef = useRef(DEFAULT_GEOHASH_PRECISION);
  const isEditingRef = useRef(false);
  const visibleLocationsRef = useRef<ImportedLocation[]>([]);
  const visibleRoutesRef = useRef<HaulRoute[]>([]);
  const blockedRoadsRef = useRef<Map<string, BlockedRoadEntry>>(new Map());
  const gradesWithZoneRef = useRef<
    Array<{ grade: GradeRecord; ring: GeoPoint[] }>
  >([]);
  const showGradesRef = useRef(true);
  const drawingRoadFromRef = useRef<ImportedLocation | null>(null);
  const editingRouteTargetRef = useRef<HaulRoute | null>(null);
  const editingRouteFromRef = useRef<ImportedLocation | null>(null);
  const editingRouteToRef = useRef<ImportedLocation | null>(null);
  const pendingRoutesRef = useRef<PendingRoute[]>([]);
  const roadWidthRef = useRef(20);
  const draftVerticesRef = useRef<GeoPoint[]>([]);
  const boundaryEditModeRef = useRef<BoundaryEditMode>('draw');
  const draggingVertexIndexRef = useRef(-1);
  const shapeDragStartPointRef = useRef<GeoPoint | null>(null);
  const shapeDragStartVerticesRef = useRef<GeoPoint[] | null>(null);
  const originalVerticesRef = useRef<GeoPoint[] | null>(null);
  const movingCentroidLocationRef = useRef<ImportedLocation | null>(null);
  const centroidDraftRef = useRef<GeoPoint | null>(null);
  // The polygon to constrain the centroid drag to, captured once when the
  // mode starts — empty for a point-only location, which drags freely.
  const centroidHullRef = useRef<GeoPoint[]>([]);
  const centroidDraggingRef = useRef(false);

  const latestTagData = useAppSelector(selectLatestTagData);
  const {
    proj4String,
    localOriginEasting,
    localOriginNorthing,
    code: customerCode,
  } = useAppSelector((s) => s.persistedReducer.customer);

  const truckMarkers = useTruckMarkers();

  const fetchLocations = useCallback(async () => {
    try {
      const data = await locationApi.getLocations();
      setLocations(data ?? []);
    } catch {
      setLocations([]);
    }
  }, [locationApi]);

  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  const fetchRoutes = useCallback(async () => {
    try {
      const data = await haulRouteApi.getRoutes();
      setRoutes(data ?? []);
    } catch {
      setRoutes([]);
    }
  }, [haulRouteApi]);

  useEffect(() => {
    void fetchRoutes();
  }, [fetchRoutes]);

  const fetchBlockedRoads = useCallback(async () => {
    try {
      const data = await dispatchMonitorApi.getBlockedRoads();
      // `ttlSeconds` is "remaining as of this response", not a fixed
      // duration from `blockedAt` — stamp an absolute deadline now so
      // later consumers (the expiry sweep below, the hover countdown)
      // have a fixed target instead of recomputing (and drifting) from a
      // stale relative value between polls.
      const fetchedAt = Date.now();
      setBlockedRoads(
        new Map(
          data.map((road) => [
            road.roadId,
            {
              ...road,
              expiresAt:
                road.ttlSeconds == null
                  ? null
                  : fetchedAt + road.ttlSeconds * 1000,
            },
          ])
        )
      );
    } catch {
      setBlockedRoads(new Map());
    }
  }, [dispatchMonitorApi]);

  useEffect(() => {
    void fetchBlockedRoads();
    const interval = setInterval(() => {
      void fetchBlockedRoads();
    }, 30000);
    return () => {
      clearInterval(interval);
    };
  }, [fetchBlockedRoads]);

  // The 30s poll above is the source of truth (also catches another user
  // unblocking a road, or an indefinite block being lifted), but waiting up
  // to 30s after a TTL actually elapses to reflect it reads as broken —
  // this sweep drops expired entries from local state the second their
  // countdown hits zero, no server round-trip needed. Indefinite blocks
  // (expiresAt: null) never expire here; only the DELETE call clears them.
  useEffect(() => {
    const interval = setInterval(() => {
      setBlockedRoads((prev) => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        prev.forEach((road, roadId) => {
          if (road.expiresAt != null && now >= road.expiresAt) {
            next.delete(roadId);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const fetchGrades = useCallback(async () => {
    try {
      const data = await gradeApi.getGrades();
      setGrades(data ?? []);
    } catch {
      setGrades([]);
    }
  }, [gradeApi]);

  useEffect(() => {
    void fetchGrades();
  }, [fetchGrades]);

  // A location renders on the map if it has a centroid OR a drawn boundary
  // (zoneGeog) to derive one from — requiring centroid alone was dropping
  // locations (e.g. stockpiles) that were only ever boundary-drawn.
  const activeLocations = useMemo(
    () =>
      locations?.filter(
        (loc) =>
          loc.active &&
          ((loc.centroidLat != null && loc.centroidLon != null) ||
            locationRing(loc).length >= 3)
      ) ?? [],
    [locations]
  );

  // Same as activeLocations but without the active filter — used to show
  // inactive locations dimmed on the map instead of hiding them entirely.
  const visibleLocations = useMemo(
    () =>
      locations?.filter(
        (loc) =>
          (loc.centroidLat != null && loc.centroidLon != null) ||
          locationRing(loc).length >= 3
      ) ?? [],
    [locations]
  );

  // Duplicate-name check for the Add Location dialog — matches against every
  // location regardless of active status (an inactive one still owns its name).
  const locationNames = useMemo(
    () => locations?.map((loc) => loc.name) ?? [],
    [locations]
  );

  const mineSiteCenter = useMemo<{ lat: number; lon: number } | null>(() => {
    // Prefer centering on imported locations; fall back to live truck GPS
    const locationsWithCentroid = activeLocations.filter(
      (loc) => loc.centroidLat != null && loc.centroidLon != null
    );
    if (locationsWithCentroid.length > 0) {
      const avgLat =
        locationsWithCentroid.reduce((s, l) => s + (l.centroidLat ?? 0), 0) /
        locationsWithCentroid.length;
      const avgLon =
        locationsWithCentroid.reduce((s, l) => s + (l.centroidLon ?? 0), 0) /
        locationsWithCentroid.length;
      return { lat: avgLat, lon: avgLon };
    }
    const points = Object.values(latestTagData)
      .map((s) => ({
        lat: Number(s.gps_latitude),
        lon: Number(s.gps_longitude),
      }))
      .filter(
        (p) =>
          isFinite(p.lat) &&
          isFinite(p.lon) &&
          p.lat !== 0 &&
          p.lon !== 0 &&
          p.lat >= -90 &&
          p.lat <= 90 &&
          p.lon >= -180 &&
          p.lon <= 180
      );
    if (points.length === 0) return null;
    const avgLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const avgLon = points.reduce((s, p) => s + p.lon, 0) / points.length;
    return { lat: avgLat, lon: avgLon };
  }, [activeLocations, latestTagData]);

  // A padded bounding box around every location's drawn boundary (falling
  // back to just its centroid where no boundary has been drawn yet) — used
  // centroid point.
  const mineSiteBounds = useMemo(() => {
    const locationsWithCentroid = activeLocations.filter(
      (loc) => loc.centroidLat != null && loc.centroidLon != null
    );
    if (locationsWithCentroid.length > 0) {
      const boundaryPoints = locationsWithCentroid.flatMap((loc) => {
        const ring = locationRing(loc);
        return ring.length > 0
          ? ring
          : [
              {
                lon: loc.centroidLon as number,
                lat: loc.centroidLat as number,
              },
            ];
      });
      return getPaddedLonLatBounds(boundaryPoints, 0.6, 0.008);
    }
    const points = Object.values(latestTagData)
      .map((s) => ({
        lat: Number(s.gps_latitude),
        lon: Number(s.gps_longitude),
      }))
      .filter(
        (p) =>
          isFinite(p.lat) &&
          isFinite(p.lon) &&
          p.lat !== 0 &&
          p.lon !== 0 &&
          p.lat >= -90 &&
          p.lat <= 90 &&
          p.lon >= -180 &&
          p.lon <= 180
      );
    if (points.length === 0) return null;
    return getPaddedLonLatBounds(points, 0.6, 0.008);
  }, [activeLocations, latestTagData]);

  // Lets any customer switch the basemap between the plain neutral-road map
  // (default) and satellite imagery — replaces Cesium's baseLayerPicker
  // widget with a plain MUI toggle, matching the pattern already used by
  // FleetOverview's AssetMap for the same satellite/neutral-road switch.
  const applyBasemap = useCallback(
    (map: NonNullable<ReturnType<MapRef['getMap']>>, mode: BasemapId) => {
      if (mode === 'satellite' && !hasNoSatelliteCoverage(customerCode)) {
        setBasemap(map, 'satellite');
        map.setMaxZoom(SATELLITE_TOGGLE_MAX_ZOOM);
      } else {
        applyNeutralBackdrop(map);
        map.setMaxZoom(MINE_MAP_MAX_ZOOM);
      }
    },
    [customerCode]
  );

  const applyDefaultBasemap = useCallback(
    (map: NonNullable<ReturnType<MapRef['getMap']>>) => {
      applyBasemap(map, mapBasemap);
    },
    [applyBasemap, mapBasemap]
  );

  const handleBasemapToggle = useCallback(
    (mode: BasemapId) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      applyBasemap(map, mode);
      setMapBasemap(
        mode === 'satellite' && hasNoSatelliteCoverage(customerCode)
          ? 'neutralRoad'
          : mode
      );
    },
    [applyBasemap, customerCode]
  );

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    applyDefaultBasemap(map);
    setMapReady(true);
  }, [applyDefaultBasemap]);

  useMineMapOverlay({
    mapRef,
    enabled: customerCode === CAR_CUSTOMER_CODE,
    mapReady,
    showRoadsOverlay,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
  });

  // Normalizes whatever the file actually contains (a bare Geometry, a
  // single Feature, or a proper FeatureCollection) into a FeatureCollection
  // so the render side only ever has one shape to deal with.
  const GEOJSON_GEOMETRY_TYPES = [
    'Point',
    'LineString',
    'Polygon',
    'MultiPoint',
    'MultiLineString',
    'MultiPolygon',
    'GeometryCollection',
  ];
  const toFeatureCollection = (
    parsed: unknown
  ): LocalGeoJsonFeatureCollection | null => {
    if (typeof parsed !== 'object' || parsed === null) return null;
    const value = parsed as { type?: unknown };
    if (value.type === 'FeatureCollection') {
      const collection = parsed as LocalGeoJsonFeatureCollection;
      return Array.isArray(collection.features) ? collection : null;
    }
    type LocalFeature = LocalGeoJsonFeatureCollection['features'][number];
    if (value.type === 'Feature') {
      return {
        type: 'FeatureCollection',
        features: [parsed as LocalFeature],
      };
    }
    if (
      typeof value.type === 'string' &&
      GEOJSON_GEOMETRY_TYPES.includes(value.type)
    ) {
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: parsed as LocalFeature['geometry'],
          },
        ],
      };
    }
    return null;
  };

  const handleGeoJsonFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        try {
          const text = loadEvent.target?.result;
          const parsed = JSON.parse(String(text));
          const collection = toFeatureCollection(parsed);
          if (!collection || collection.features.length === 0) {
            throw new Error('invalid geojson');
          }
          setImportedGeoJson(collection);
          setImportedGeoJsonName(file.name);
          const center = geoJsonCentroid(collection);
          if (center) flyTo(center.lat, center.lon, 14, 1000);
          dispatch(
            showNotification({
              message: t('roads_importer_geojson_import_success', {
                name: file.name,
              }),
              type: 'success',
            })
          );
        } catch {
          dispatch(
            showNotification({
              message: t('roads_importer_geojson_import_error'),
              type: 'error',
            })
          );
        }
      };
      reader.readAsText(file);
    },
    [dispatch, flyTo, t]
  );

  const handleClearImportedGeoJson = useCallback(() => {
    setImportedGeoJson(null);
    setImportedGeoJsonName(null);
  }, []);

  useEffect(() => {
    if (!mapReady || hasCenteredRef.current) return;
    if (!mineSiteCenter) return;
    hasCenteredRef.current = true;
    // Land zoomed in close to the site, not a wide site-fit view.
    flyTo(mineSiteCenter.lat, mineSiteCenter.lon, 16, 1500);
  }, [mapReady, mineSiteCenter, flyTo]);

  // Locks panning/zoom-out to the site itself and caps zoom-in — same
  // <MapView maxBounds>/<MapView maxZoom> pattern FleetOverview's AssetMap
  // uses for the same restriction. Passed declaratively (not an imperative
  // map.setMaxBounds() call) so react-maplibre keeps it applied any time the
  // underlying map re-initializes.
  const maxBoundsProp = useMemo(
    () => (mineSiteBounds ? boundsToMaxBoundsTuple(mineSiteBounds) : undefined),
    [mineSiteBounds]
  );

  useEffect(() => {
    isEditingRef.current =
      editingLocation != null ||
      creatingLocation != null ||
      drawingRoadFrom != null ||
      editingRouteTarget != null;
    drawingRoadFromRef.current = drawingRoadFrom;
    editingRouteTargetRef.current = editingRouteTarget;
    editingRouteFromRef.current = editingRouteFrom;
    editingRouteToRef.current = editingRouteTo;
  }, [
    editingLocation,
    creatingLocation,
    drawingRoadFrom,
    editingRouteTarget,
    editingRouteFrom,
    editingRouteTo,
  ]);

  useEffect(() => {
    visibleLocationsRef.current = visibleLocations;
  }, [visibleLocations]);

  useEffect(() => {
    pendingRoutesRef.current = pendingRoutes;
  }, [pendingRoutes]);

  useEffect(() => {
    const width = parseFloat(roadWidth);
    roadWidthRef.current = isFinite(width) && width > 0 ? width : 10;
  }, [roadWidth]);

  useEffect(() => {
    draftVerticesRef.current = draftVertices;
  }, [draftVertices]);

  useEffect(() => {
    movingCentroidLocationRef.current = movingCentroidLocation;
  }, [movingCentroidLocation]);

  useEffect(() => {
    centroidDraftRef.current = centroidDraft;
  }, [centroidDraft]);

  useEffect(() => {
    boundaryEditModeRef.current = boundaryEditMode;
  }, [boundaryEditMode]);

  const visibleRoutes = useMemo(() => {
    if (!showRoutes && !routeFilterLocation) return [];
    return routes.filter(
      (route) =>
        route.routeId !== editingRouteTarget?.routeId &&
        (!routeFilterLocation ||
          route.fromLocationId === routeFilterLocation.locationId)
    );
  }, [routes, showRoutes, routeFilterLocation, editingRouteTarget]);

  useEffect(() => {
    visibleRoutesRef.current = visibleRoutes;
  }, [visibleRoutes]);

  useEffect(() => {
    blockedRoadsRef.current = blockedRoads;
  }, [blockedRoads]);

  // A from/to/via change invalidates whatever was found before — clearing
  // results (not just the selection) also hides the "no route found" empty
  // state until the user actually searches again with the new fields.
  const resetFindRouteResults = useCallback(() => {
    setFindRouteResults(null);
    setFindRouteSelectedIndex(null);
  }, []);

  const closeFindRoute = useCallback(() => {
    setFindRouteOpen(false);
    setFindRouteFrom(null);
    setFindRouteTo(null);
    setFindRouteVia([]);
    resetFindRouteResults();
  }, [resetFindRouteResults]);

  const handleFindRoutes = useCallback(async () => {
    if (!findRouteFrom || !findRouteTo) return;
    setFindRouteLoading(true);
    setFindRouteSelectedIndex(null);
    try {
      const data = await haulRouteApi.getRoutePaths(
        findRouteFrom.locationId,
        findRouteTo.locationId,
        {
          via: findRouteVia.map((loc) => loc.locationId),
          all: false,
        }
      );
      setFindRouteResults(data ?? []);
      if (data?.length) setFindRouteSelectedIndex(0);
    } catch {
      setFindRouteResults(null);
      dispatch(
        showNotification({
          message: t('roads_importer_find_route_failed'),
          type: 'error',
        })
      );
    } finally {
      setFindRouteLoading(false);
    }
  }, [findRouteFrom, findRouteTo, findRouteVia, haulRouteApi, dispatch, t]);

  // The map-drawable path for whichever result is selected — null whenever
  // nothing's selected, which also doubles as "hide the highlight".
  const foundRoutePath = useMemo(() => {
    const option =
      findRouteSelectedIndex != null
        ? findRouteResults?.[findRouteSelectedIndex]
        : null;
    if (!option) return null;
    const path = buildRouteOptionPath(option, routes, locations);
    return path.length >= 2 ? path : null;
  }, [findRouteSelectedIndex, findRouteResults, routes, locations]);

  // Frames the whole selected route's extent — replaces the old flyToPath's
  // manual "compute an altitude from the path's lat/lon span" math with
  // fitBounds, which already computes the ideal zoom to fit an arbitrary
  // bounding box natively.
  useEffect(() => {
    if (!foundRoutePath) return;
    fitBounds(foundRoutePath, 60, 1500);
  }, [foundRoutePath, fitBounds]);

  // The stops on the selected Find Route result — used to isolate the map
  // to just this route and its locations while it's showing, the same way
  // "Move Centroid" isolates the map to one location. Every other saved
  // route and location is otherwise just visual noise on top of the one the
  // user actually asked to see.
  const foundRouteLocationIds = useMemo(() => {
    const option =
      findRouteSelectedIndex != null
        ? findRouteResults?.[findRouteSelectedIndex]
        : null;
    return option ? new Set(option.locationIds) : null;
  }, [findRouteSelectedIndex, findRouteResults]);

  const foundRouteGeoJson = useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (!foundRoutePath) return null;
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: foundRoutePath.map((p) => [p.lon, p.lat]),
          },
        },
      ],
    };
  }, [foundRoutePath]);

  // Grades that carry a drawn polygon (zoneGeog) — the only ones that can
  // render on / be picked from the map.
  const gradesWithZone = useMemo(
    () =>
      grades
        .map((grade) => ({ grade, ring: zoneGeogToGeoPoints(grade.zoneGeog) }))
        .filter(({ ring }) => ring.length >= 3),
    [grades]
  );

  useEffect(() => {
    gradesWithZoneRef.current = gradesWithZone;
  }, [gradesWithZone]);

  useEffect(() => {
    showGradesRef.current = showGrades;
  }, [showGrades]);

  // Grade blocks whose center falls inside the right-clicked location's
  // boundary and aren't already mapped to it — candidates offered by the
  // "Map Grade Blocks" context-menu action.
  const mapGradeBlocksCandidates = useMemo(() => {
    if (!mapGradeBlocksTarget) return [];
    const hull = locationRing(mapGradeBlocksTarget);
    if (hull.length < 3) return [];
    return gradesWithZone
      .filter(({ grade, ring }) => {
        if (grade.locationId === mapGradeBlocksTarget.locationId) return false;
        const centroidLat = ring.reduce((s, p) => s + p.lat, 0) / ring.length;
        const centroidLon = ring.reduce((s, p) => s + p.lon, 0) / ring.length;
        return pointInGeoRing(centroidLat, centroidLon, hull);
      })
      .map(({ grade }) => grade);
  }, [mapGradeBlocksTarget, gradesWithZone]);

  const confirmMapGradeBlocks = useCallback(
    async (gradeIds: string[]) => {
      const target = mapGradeBlocksTarget;
      if (!target || gradeIds.length === 0) return;
      const idSet = new Set(gradeIds);
      const results = await Promise.allSettled(
        grades
          .filter((grade) => idSet.has(grade.id))
          .map(async (grade) => {
            const { id, ...rest } = grade;
            await gradeApi.updateGrade(id, {
              ...rest,
              locationId: target.locationId,
              locationName: target.name,
            });
          })
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = results.length - failed;
      await fetchGrades();
      setMapGradeBlocksTarget(null);
      dispatch(
        showNotification({
          message:
            failed === 0
              ? t('roads_importer_map_grade_blocks_success', {
                  count: succeeded,
                  name: target.name,
                })
              : t('roads_importer_map_grade_blocks_partial', {
                  succeeded,
                  failed,
                }),
          type: failed === 0 ? 'success' : 'error',
        })
      );
    },
    [mapGradeBlocksTarget, grades, gradeApi, fetchGrades, dispatch, t]
  );

  // Grade blocks currently mapped to the location being edited whose center
  // no longer falls inside the boundary as just redrawn/moved — saving would
  // otherwise silently leave them pointing at a shape they're no longer in.
  const gradesToDeallocate = useMemo(() => {
    if (!editingLocation || draftVertices.length < 3) return [];
    return grades.filter((grade) => {
      if (grade.locationId !== editingLocation.locationId) return false;
      const ring = zoneGeogToGeoPoints(grade.zoneGeog);
      if (ring.length < 3) return false;
      const centroidLat = ring.reduce((s, p) => s + p.lat, 0) / ring.length;
      const centroidLon = ring.reduce((s, p) => s + p.lon, 0) / ring.length;
      return !pointInGeoRing(centroidLat, centroidLon, draftVertices);
    });
  }, [editingLocation, draftVertices, grades]);

  // ── Map interactions ──
  // Ported from a single Cesium ScreenSpaceEventHandler (one callback per
  // event type, since Cesium only allows one) to MapLibre's native map
  // events — same consolidated-dispatcher shape, since every concern below
  // (location boundary drawing, road drawing, vertex/shape dragging, "Move
  // Centroid" dragging, saved-route hover) still has to share one `click`,
  // one `contextmenu`, one `mousedown`, one `mousemove`, and one `mouseup`
  // listener the same way it shared Cesium's handler. See this folder's
  // CLAUDE.md for why this can't be split into independent per-concern
  // effects. Every mode flag/ref (isEditingRef, boundaryEditModeRef,
  // drawingRoadFromRef, movingCentroidLocationRef, ...) is unchanged from
  // the Cesium version — only the event source and coordinate extraction
  // change (event.lngLat instead of camera.pickEllipsoid + Cartographic,
  // which is always valid here since MapLibre is a flat 2D map with no
  // "looking off into space" case Cesium's ellipsoid pick could miss).
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const toGeoPoint = (lngLat: { lat: number; lng: number }): GeoPoint => ({
      lat: lngLat.lat,
      lon: lngLat.lng,
    });

    const handleClick = (event: MapMouseEvent) => {
      // "Move Centroid" mode owns left-click/down/move/up entirely — it's
      // deliberately kept out of isEditingRef (see mousedown/mousemove/
      // mouseup below) since folding it in would also feed clicks into the
      // draw-a-boundary-point branch further down this same handler.
      if (movingCentroidLocationRef.current) return;
      if (!isEditingRef.current) {
        // Not editing: a left-click on a grade zone opens its details popover.
        if (showGradesRef.current) {
          const point = toGeoPoint(event.lngLat);
          const hit = gradesWithZoneRef.current.find(({ ring }) =>
            pointInGeoRing(point.lat, point.lon, ring)
          );
          if (hit) {
            const rect = map.getContainer().getBoundingClientRect();
            setGradeDetailsTarget({
              mouseX: rect.left + event.point.x,
              mouseY: rect.top + event.point.y,
              grade: hit.grade,
            });
          }
        }
        return;
      }
      // Vertex/Shape move mode owns left-click, don't also add a point
      if (
        boundaryEditModeRef.current !== 'draw' &&
        !drawingRoadFromRef.current
      ) {
        return;
      }
      const point = toGeoPoint(event.lngLat);

      // Road drawing: clicking near another location's center finishes the
      // road there. Center-proximity, not point-in-polygon — zones can sit
      // close to or overlap each other, so testing the whole boundary risks
      // landing the road on the wrong neighboring location. The "center" is
      // the same hull-derived point the location's label renders at (not
      // necessarily the raw stored centroid, which can be missing or
      // offset), so what's visually there is what actually gets hit.
      const roadFrom = drawingRoadFromRef.current;
      if (roadFrom) {
        let destinationCenter: GeoPoint | null = null;
        // Inactive locations are valid road destinations too — a road
        // isn't tied to whether the location is currently active.
        const destination = visibleLocationsRef.current.find((loc) => {
          if (loc.locationId === roadFrom.locationId) return false;
          const center = locationCenterPoint(loc);
          if (!center) return false;
          if (
            haversineMeters(point, center) > ROAD_DESTINATION_HIT_RADIUS_METERS
          ) {
            return false;
          }
          destinationCenter = center;
          return true;
        });
        if (destination) {
          setDraftVertices((prev) => [...prev, destinationCenter ?? point]);
          setRoadVertexWidths((prev) => [...prev, roadWidthRef.current]);
          setRoadTo(destination);
          return;
        }
      }

      setDraftVertices((prev) => [...prev, point]);
      if (roadFrom) {
        setRoadVertexWidths((prev) => [...prev, roadWidthRef.current]);
      }
    };

    // Right-click handling: MapLibre's own 'contextmenu' map event is
    // already sourced from the canvas's native browser contextmenu event —
    // the same robust trigger the old Cesium code deliberately opted into
    // instead of Cesium's synthesized RIGHT_CLICK (which some trackpad
    // "right-click" gestures didn't reliably produce) — so no separate
    // manual canvas listener is needed anymore.
    const handleRightClick = (event: MapMouseEvent) => {
      event.preventDefault();
      if (movingCentroidLocationRef.current) return;
      if (isEditingRef.current) {
        // Route path editing only moves existing joints — no point to undo,
        // and popping one here would silently delete an endpoint.
        if (editingRouteTargetRef.current) return;
        // Undo the last boundary point
        setDraftVertices((prev) => prev.slice(0, -1));
        if (drawingRoadFromRef.current) {
          setRoadVertexWidths((prev) => prev.slice(0, -1));
        }
        return;
      }

      const point = toGeoPoint(event.lngLat);
      rightClickPointRef.current = point;

      // Not editing: right-click opens the node menu, the route menu,
      // or the add-location menu on empty ground. Hit-tests against
      // visibleLocationsRef (not just active ones) so an inactive location
      // shown dimmed on the map can still be right-clicked to edit/delete.
      // When the click lands inside more than one location's boundary (a
      // small location nested inside/near a larger one), the smallest
      // matching polygon wins — otherwise a click meant for the nested
      // location always resolved to whichever larger "parent" also
      // contained that point.
      const locationCandidates = visibleLocationsRef.current
        .map((loc) => ({ loc, hull: locationRing(loc) }))
        .filter(
          ({ hull }) =>
            hull.length >= 3 && pointInGeoRing(point.lat, point.lon, hull)
        );
      const target = locationCandidates.length
        ? locationCandidates.reduce((smallest, cur) =>
            ringAreaApprox(cur.hull) < ringAreaApprox(smallest.hull)
              ? cur
              : smallest
          ).loc
        : undefined;
      let routeTarget: HaulRoute | null = null;
      let pendingRouteIndex: number | null = null;
      if (!target) {
        // Pending (queued, not-yet-saved) roads: hit within the road width
        pendingRouteIndex = pendingRoutesRef.current.findIndex((road) => {
          for (let i = 0; i < road.path.length - 1; i++) {
            const segWidth =
              road.widths[i + 1] ?? road.widths[road.widths.length - 1] ?? 10;
            const dist = distToSegmentMeters(
              point,
              road.path[i],
              road.path[i + 1]
            );
            if (dist <= segWidth / 2 + 5) return true;
          }
          return false;
        });
        if (pendingRouteIndex < 0) pendingRouteIndex = null;

        if (pendingRouteIndex == null) {
          routeTarget =
            visibleRoutesRef.current.find((route) => {
              const precision =
                route.geohashes?.[0]?.length ?? DEFAULT_GEOHASH_PRECISION;
              const gh = encodeGeohash(point.lat, point.lon, precision);
              return route.geohashes?.includes(gh);
            }) ?? null;
        }
      }
      // Right-click always opens a menu right where the hover tooltip would
      // be — drop it so the two don't stack on top of each other.
      hoveredRouteNamesKeyRef.current = null;
      setHoveredRoutesTarget(null);
      const rect = map.getContainer().getBoundingClientRect();
      setContextMenu({
        mouseX: rect.left + event.point.x,
        mouseY: rect.top + event.point.y,
        location: target ?? null,
        route: routeTarget,
        pendingRouteIndex,
      });
    };

    // Vertex mode (boundary drawing only): mousedown grabs the nearest
    // vertex. Shape mode: mousedown inside the polygon grabs the whole
    // shape. "Move Centroid" mode: mousedown on the marker grabs it for
    // dragging — handled first and returns, since only one listener set is
    // registered per event name here (see the CLAUDE.md note on the shared
    // handler). Vertices/the centroid marker are drawn as plain
    // pointer-events:none <Marker>s (see RoadDistanceLabel's doc comment
    // above) precisely so this manual pixel hit-test — not a native
    // draggable Marker — is what grabs them.
    const handleMouseDown = (event: MapMouseEvent) => {
      if (movingCentroidLocationRef.current) {
        const draft = centroidDraftRef.current;
        if (!draft) return;
        const screen = map.project([draft.lon, draft.lat]);
        // The billboard is bottom/center-anchored to `screen` (its tip
        // touches the ground point, the icon extends upward from there) —
        // a small circle around that anchor missed most real clicks, which
        // land on the visible bulb above it. Hit-test the actual rendered
        // rectangle instead, with a little slack.
        const halfWidth = CENTROID_PIN_ICON_PX / 2 + 6;
        const dx = event.point.x - screen.x;
        const dy = event.point.y - screen.y;
        const withinIcon =
          Math.abs(dx) <= halfWidth &&
          dy <= 8 &&
          dy >= -(CENTROID_PIN_ICON_PX + 8);
        if (withinIcon) {
          centroidDraggingRef.current = true;
          map.dragPan.disable();
        }
        return;
      }
      if (!isEditingRef.current) return;
      if (drawingRoadFromRef.current) return;
      const mode = boundaryEditModeRef.current;
      if (mode === 'draw') return;
      const verts = draftVerticesRef.current;
      if (verts.length === 0) return;

      if (mode === 'vertex') {
        let minDist = Infinity;
        let minIdx = -1;
        verts.forEach((v, i) => {
          const screen = map.project([v.lon, v.lat]);
          const dx = screen.x - event.point.x;
          const dy = screen.y - event.point.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minDist) {
            minDist = dist;
            minIdx = i;
          }
        });

        if (minDist <= DRAG_HIT_PX && minIdx >= 0) {
          draggingVertexIndexRef.current = minIdx;
          map.dragPan.disable();
          setIsDraggingVertex(true);
        }
        return;
      }

      // mode === 'shape'
      if (verts.length < 3) return;
      const point = toGeoPoint(event.lngLat);
      if (!pointInGeoRing(point.lat, point.lon, verts)) return;
      shapeDragStartPointRef.current = point;
      shapeDragStartVerticesRef.current = verts;
      map.dragPan.disable();
      setIsDraggingVertex(true);
    };

    // Vertex mode: mousemove drags the grabbed vertex to the new position.
    // Shape mode: mousemove translates every vertex by the same offset.
    const handleMouseMove = (event: MapMouseEvent) => {
      const point = toGeoPoint(event.lngLat);

      if (movingCentroidLocationRef.current) {
        if (centroidDraggingRef.current) {
          const hull = centroidHullRef.current;
          // No polygon (point-only location) drags freely; otherwise
          // freeze at the last valid point once the cursor would take it
          // outside the boundary, rather than following it out.
          if (hull.length < 3 || pointInGeoRing(point.lat, point.lon, hull)) {
            centroidDraftRef.current = point;
            setCentroidDraft(point);
          }
        }
        return;
      }

      if (draggingVertexIndexRef.current >= 0) {
        const idx = draggingVertexIndexRef.current;
        setDraftVertices((prev) => {
          if (idx >= prev.length) return prev;
          const updated = [...prev];
          updated[idx] = point;
          return updated;
        });
        return;
      }

      const start = shapeDragStartPointRef.current;
      const startVerts = shapeDragStartVerticesRef.current;
      if (!start || !startVerts) {
        // No drag in progress: hover feedback showing which saved
        // route(s) sit under the cursor. Throttled since mousemove fires
        // far more often than the geohash hit-test needs to run, and
        // skipped entirely while drawing/editing so it doesn't distract
        // from that workflow.
        if (isEditingRef.current) return;
        const now = performance.now();
        if (now - lastRouteHoverAtRef.current < ROUTE_HOVER_THROTTLE_MS) {
          return;
        }
        lastRouteHoverAtRef.current = now;

        const seen = new Map<string, RoadHoverEntry>();
        const precision =
          visibleRoutesRef.current[0]?.geohashes?.[0]?.length ??
          DEFAULT_GEOHASH_PRECISION;
        const gh = encodeGeohash(point.lat, point.lon, precision);
        visibleRoutesRef.current.forEach((route) => {
          const routePrecision = route.geohashes?.[0]?.length ?? precision;
          const routeGh =
            routePrecision === precision
              ? gh
              : encodeGeohash(point.lat, point.lon, routePrecision);
          if (!route.geohashes?.includes(routeGh)) return;
          const label = formatRoadName(route.roadName ?? route.routeKey);
          const blockedRoad = blockedRoadsRef.current.get(route.routeId);
          if (seen.has(label)) return;
          seen.set(label, {
            key: label,
            label,
            blocked: blockedRoad != null,
            blockedReason: blockedRoad?.reason,
            blockedExpiresAt: blockedRoad ? blockedRoad.expiresAt : undefined,
          });
        });
        const entries = Array.from(seen.values());
        const key = entries
          .map((entry) => `${entry.key}:${entry.blocked}`)
          .join(',');
        if (entries.length === 0) {
          if (hoveredRouteNamesKeyRef.current !== null) {
            hoveredRouteNamesKeyRef.current = null;
            setHoveredRoutesTarget(null);
          }
          return;
        }
        hoveredRouteNamesKeyRef.current = key;
        const rect = map.getContainer().getBoundingClientRect();
        setHoveredRoutesTarget({
          mouseX: rect.left + event.point.x,
          mouseY: rect.top + event.point.y,
          entries,
        });
        return;
      }
      if (hoveredRouteNamesKeyRef.current !== null) {
        hoveredRouteNamesKeyRef.current = null;
        setHoveredRoutesTarget(null);
      }
      const dLat = point.lat - start.lat;
      const dLon = point.lon - start.lon;
      setDraftVertices(
        startVerts.map((v) => ({ lat: v.lat + dLat, lon: v.lon + dLon }))
      );
      // Lets any connected route's rendered line follow the shape live.
      // Measured against the original (pre-edit) vertices — not this drag
      // session's start point — so the offset stays correct across
      // multiple separate drag sessions on the same shape.
      const original = originalVerticesRef.current;
      if (original?.[0]) {
        setShapeDragDelta({
          lat: startVerts[0].lat + dLat - original[0].lat,
          lon: startVerts[0].lon + dLon - original[0].lon,
        });
      } else {
        setShapeDragDelta({ lat: dLat, lon: dLon });
      }
    };

    // Releases whichever drag (vertex, whole-shape, or centroid) is active
    const handleMouseUp = () => {
      if (movingCentroidLocationRef.current) {
        if (centroidDraggingRef.current) {
          centroidDraggingRef.current = false;
          map.dragPan.enable();
        }
        return;
      }

      const droppedIdx = draggingVertexIndexRef.current;

      // Route path editing: an endpoint drop must always land on a
      // location — snap to whichever is nearest within the same
      // proximity radius road-drawing uses, or bounce back to whichever
      // location it's currently connected to if nothing's close enough.
      if (
        editingRouteTargetRef.current &&
        droppedIdx >= 0 &&
        (droppedIdx === 0 || droppedIdx === draftVerticesRef.current.length - 1)
      ) {
        const isFrom = droppedIdx === 0;
        const dropPoint = draftVerticesRef.current[droppedIdx];
        const otherEndLocationId = (
          isFrom ? editingRouteToRef.current : editingRouteFromRef.current
        )?.locationId;

        let closest: { loc: ImportedLocation; center: GeoPoint } | null = null;
        let closestDist = Infinity;
        visibleLocationsRef.current.forEach((loc) => {
          if (loc.locationId === otherEndLocationId) return;
          const center = locationCenterPoint(loc);
          if (!center) return;
          const dist = haversineMeters(dropPoint, center);
          if (
            dist <= ROAD_DESTINATION_HIT_RADIUS_METERS &&
            dist < closestDist
          ) {
            closest = { loc, center };
            closestDist = dist;
          }
        });

        const fallback = isFrom
          ? editingRouteFromRef.current
          : editingRouteToRef.current;
        const resolved = closest ?? {
          loc: fallback,
          center: fallback ? locationCenterPoint(fallback) : null,
        };

        if (resolved.loc && resolved.center) {
          const center = resolved.center;
          setDraftVertices((prev) => {
            const next = [...prev];
            next[droppedIdx] = center;
            return next;
          });
          if (isFrom) setEditingRouteFrom(resolved.loc);
          else setEditingRouteTo(resolved.loc);
        }
      }

      if (
        draggingVertexIndexRef.current >= 0 ||
        shapeDragStartPointRef.current
      ) {
        draggingVertexIndexRef.current = -1;
        shapeDragStartPointRef.current = null;
        shapeDragStartVerticesRef.current = null;
        // Keep shapeDragDelta as-is here — it reflects the shape's total
        // offset from its original position, and the connected route should
        // stay visually shifted along with the still-shifted draft polygon
        // until the edit is actually saved or cancelled (resetBoundaryDrag).
        map.dragPan.enable();
        setIsDraggingVertex(false);
      }
    };

    map.on('click', handleClick);
    map.on('contextmenu', handleRightClick);
    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    return () => {
      map.off('click', handleClick);
      map.off('contextmenu', handleRightClick);
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
    };
  }, [mapReady]);

  // Releases a mid-drag vertex grab and restores camera controls — needed
  // when a mode change (cancel/save/switch target) happens without a
  // mouseup event ever firing.
  const resetBoundaryDrag = useCallback(() => {
    draggingVertexIndexRef.current = -1;
    shapeDragStartPointRef.current = null;
    shapeDragStartVerticesRef.current = null;
    originalVerticesRef.current = null;
    setBoundaryEditMode('draw');
    setShapeDragDelta(null);
    setIsDraggingVertex(false);
    mapRef.current?.getMap()?.dragPan.enable();
  }, []);

  const startEditing = useCallback(
    (loc: ImportedLocation | null) => {
      resetBoundaryDrag();
      setCreatingLocation(null);
      setEditingLocation(loc);
      const verts = loc ? zoneGeogToGeoPoints(loc.zoneGeog) : [];
      setDraftVertices(verts);
      originalVerticesRef.current = loc ? verts : null;
      if (!loc) return;
      // Intersections are just a point on the map — boundary drawing and
      // per-vertex resizing don't apply, only repositioning the whole shape.
      if (loc.locationType?.toLowerCase() === 'intersection') {
        setBoundaryEditMode('shape');
      }
      const lat = loc.centroidLat;
      const lon = loc.centroidLon;
      if (lat != null && lon != null) flyTo(lat, lon, 17, 1500);
    },
    [resetBoundaryDrag, flyTo]
  );

  const startCreating = useCallback(
    (draft: NewLocationDraft) => {
      resetBoundaryDrag();
      setEditingLocation(null);
      setCreatingLocation(draft);
      // Intersections are usually just a point on the map — auto-draft a
      // small circle at the click spot instead of making the user trace a
      // polygon by hand; they can still drag/edit it before saving.
      const anchor = rightClickPointRef.current;
      const isIntersection =
        draft.locationType?.toLowerCase() === 'intersection';
      setDraftVertices(
        isIntersection && anchor ? buildCircleGeoPoints(anchor, 10) : []
      );
      // Intersections are just a point on the map — boundary drawing and
      // per-vertex resizing don't apply, only repositioning the whole shape.
      if (isIntersection) setBoundaryEditMode('shape');
    },
    [resetBoundaryDrag]
  );

  const startDrawingRoad = useCallback(
    (loc: ImportedLocation) => {
      resetBoundaryDrag();
      setEditingLocation(null);
      setCreatingLocation(null);
      setDrawingRoadFrom(loc);
      setRoadTo(null);
      setRoadRoadName('');
      setRoadReturnRoadName('');
      setRoadSameNameBothWays(false);
      setRoadMinutes('5');
      setRoadCapacity('3');
      setRoadWeightCapacity('');
      setRoadSpeedLimitMin('40');
      setRoadSpeedLimitMax('40');
      setRoadLanes('1');
      setRoadCondition('good');
      setRoadSuggestions('');
      setRoadWidth('10');
      setRoadTwoWay(true);
      editPrecisionRef.current =
        loc.geohashes?.[0]?.length ?? DEFAULT_GEOHASH_PRECISION;
      const lat = loc.centroidLat;
      const lon = loc.centroidLon;
      if (lat != null && lon != null) {
        setDraftVertices([{ lat, lon }]);
        setRoadVertexWidths([roadWidthRef.current]);
      } else {
        setDraftVertices([]);
        setRoadVertexWidths([]);
      }
    },
    [resetBoundaryDrag]
  );

  // Defaults the new road's name (and its return-leg name, for two-way
  // roads) to "from -> to" the moment its destination is picked
  // (SaveRoadDialog opens) — still freely editable there after.
  useEffect(() => {
    if (drawingRoadFrom && roadTo) {
      setRoadRoadName(`${drawingRoadFrom.name} -> ${roadTo.name}`);
      setRoadReturnRoadName(`${roadTo.name} -> ${drawingRoadFrom.name}`);
    }
  }, [drawingRoadFrom, roadTo]);

  const cancelEditing = useCallback(() => {
    resetBoundaryDrag();
    setEditingLocation(null);
    setCreatingLocation(null);
    setDrawingRoadFrom(null);
    setRoadTo(null);
    setEditingRouteTarget(null);
    setEditingRouteFrom(null);
    setEditingRouteTo(null);
    setDraftVertices([]);
    setRoadVertexWidths([]);
    setMovingCentroidLocation(null);
    setCentroidDraft(null);
    centroidDraggingRef.current = false;
  }, [resetBoundaryDrag]);

  const startMovingCentroid = useCallback(
    (loc: ImportedLocation) => {
      const center = locationCenterPoint(loc);
      if (!center) return;
      cancelEditing();
      centroidHullRef.current = locationRing(loc);
      setMovingCentroidLocation(loc);
      setCentroidDraft(center);
      flyTo(center.lat, center.lon, 17, 1500);
    },
    [cancelEditing, flyTo]
  );

  const saveCentroid = useCallback(async () => {
    const loc = movingCentroidLocation;
    const draft = centroidDraft;
    if (!loc || !draft) return;
    setSavingCentroid(true);
    try {
      await locationApi.updateLocation(loc.locationId, {
        centroidLat: draft.lat,
        centroidLon: draft.lon,
      });
      // Applied immediately rather than waiting on a refetch — same
      // instant-feedback pattern as the road path-edit save.
      setLocations((prev) =>
        prev.map((l) =>
          l.locationId === loc.locationId
            ? { ...l, centroidLat: draft.lat, centroidLon: draft.lon }
            : l
        )
      );
      dispatch(
        showNotification({
          message: t('roads_importer_move_centroid_success'),
          type: 'success',
        })
      );
      cancelEditing();
    } catch {
      dispatch(
        showNotification({
          message: t('roads_importer_move_centroid_failed'),
          type: 'error',
        })
      );
    } finally {
      setSavingCentroid(false);
    }
  }, [
    movingCentroidLocation,
    centroidDraft,
    locationApi,
    dispatch,
    t,
    cancelEditing,
  ]);

  // Rasterize the drawn road path into geohash cells — each segment is
  // painted with its own width (circular brush along the centerline)
  const rasterizeRoadPath = useCallback(
    (path: GeoPoint[], precision: number, widths: number[]): string[] => {
      if (path.length < 2) return [];
      const anchor = decodeGeohashBounds(
        encodeGeohash(path[0].lat, path[0].lon, precision)
      );
      const dLat = anchor.maxLat - anchor.minLat;
      const dLon = anchor.maxLon - anchor.minLon;
      if (dLat <= 0 || dLon <= 0) return [];
      const cellMeters = dLat * 111320;
      const out = new Set<string>();
      const paint = (lat: number, lon: number, halfCells: number) => {
        for (let di = -halfCells; di <= halfCells; di++) {
          for (let dj = -halfCells; dj <= halfCells; dj++) {
            if (di * di + dj * dj > halfCells * halfCells + 0.5) continue;
            out.add(encodeGeohash(lat + di * dLat, lon + dj * dLon, precision));
          }
        }
      };
      for (let i = 0; i < path.length - 1; i++) {
        const a = path[i];
        const b = path[i + 1];
        const segWidth = widths[i + 1] ?? widths[widths.length - 1] ?? 50;
        const halfCells = Math.max(0, Math.round(segWidth / 2 / cellMeters));
        const cellsSpanned = Math.max(
          Math.abs(b.lat - a.lat) / dLat,
          Math.abs(b.lon - a.lon) / dLon
        );
        const steps = Math.max(1, Math.ceil(cellsSpanned * 2));
        for (let s = 0; s <= steps; s++) {
          paint(
            a.lat + ((b.lat - a.lat) * s) / steps,
            a.lon + ((b.lon - a.lon) * s) / steps,
            halfCells
          );
        }
      }
      return Array.from(out);
    },
    []
  );

  // Per-segment width, keyed by the geohash of each segment's endpoints —
  // e.g. a road drawn 10m -> 5m -> 20m becomes three { from, to, width }
  // entries. Plain JSON, so it stores as-is in a Postgres jsonb column.
  const buildWidthSegments = useCallback(
    (
      path: GeoPoint[],
      precision: number,
      widths: number[]
    ): Array<{ from: string; to: string; width: number }> => {
      if (path.length < 2) return [];
      const pointGeohashes = path.map((v) =>
        encodeGeohash(v.lat, v.lon, precision)
      );
      const segments: Array<{ from: string; to: string; width: number }> = [];
      for (let i = 0; i < path.length - 1; i++) {
        const width = widths[i + 1] ?? widths[widths.length - 1] ?? 50;
        segments.push({
          from: pointGeohashes[i],
          to: pointGeohashes[i + 1],
          width,
        });
      }
      return segments;
    },
    []
  );

  // Finish drawing → add to the pending queue. Nothing hits the API yet;
  // geohashes are computed lazily at save time so late width edits count.
  const queueRoad = useCallback(() => {
    const from = drawingRoadFrom;
    const to = roadTo;
    if (!from || !to || draftVertices.length < 2) return;
    const minutes = parseInt(roadMinutes, 10);
    const capacity = parseInt(roadCapacity, 10);
    const weightCapacity = parseFloat(roadWeightCapacity);
    const speedLimitMin = parseFloat(roadSpeedLimitMin);
    const speedLimitMax = parseFloat(roadSpeedLimitMax);
    const lanes = parseInt(roadLanes, 10);
    const key = `${from.name},${to.name}`;
    const shared = {
      widths: roadVertexWidths,
      precision: editPrecisionRef.current,
      minutes: isFinite(minutes) ? minutes : 5,
      truckCapacity: isFinite(capacity) ? capacity : 3,
      weightCapacityInTon: isFinite(weightCapacity) ? weightCapacity : null,
      speedLimitMinKph: isFinite(speedLimitMin) ? speedLimitMin : 40,
      speedLimitMaxKph: isFinite(speedLimitMax) ? speedLimitMax : 40,
      numberOfLanes: isFinite(lanes) ? lanes : 1,
      roadCondition,
      geoEngineerSuggestions: roadSuggestions,
    };
    const trimmedRoadName = roadRoadName.trim() || undefined;
    const roadName = trimmedRoadName ?? `${from.name} -> ${to.name}`;
    const trimmedReturnRoadName = roadReturnRoadName.trim() || undefined;
    const returnRoadName = roadSameNameBothWays
      ? roadName
      : trimmedReturnRoadName ?? `${to.name} -> ${from.name}`;
    setPendingRoutes((prev) => [
      ...prev,
      {
        ...shared,
        key: `${key}-${prev.length}`,
        path: draftVertices,
        fromLocationName: from.name,
        toLocationName: to.name,
        roadName,
      },
      ...(roadTwoWay
        ? [
            {
              ...shared,
              key: `${to.name},${from.name}-${prev.length + 1}`,
              path: [...draftVertices].reverse(),
              widths: reverseRoadWidths(roadVertexWidths),
              fromLocationName: to.name,
              toLocationName: from.name,
              roadName: returnRoadName,
            },
          ]
        : []),
    ]);
    dispatch(
      showNotification({
        message: t('roads_importer_route_queued', {
          from: from.name,
          to: to.name,
        }),
        type: 'success',
      })
    );
    cancelEditing();
  }, [
    drawingRoadFrom,
    roadTo,
    draftVertices,
    roadVertexWidths,
    roadRoadName,
    roadReturnRoadName,
    roadSameNameBothWays,
    roadMinutes,
    roadCapacity,
    roadWeightCapacity,
    roadSpeedLimitMin,
    roadSpeedLimitMax,
    roadLanes,
    roadCondition,
    roadSuggestions,
    roadTwoWay,
    dispatch,
    t,
    cancelEditing,
  ]);

  // POST every pending road in parallel — one request each. Failures stay
  // queued so the user can just click Save All again after fixing the issue.
  const saveAllRoutes = useCallback(async () => {
    if (pendingRoutes.length === 0) return;
    setSavingAllRoutes(true);
    const results = await Promise.allSettled(
      pendingRoutes.map(async (road) => {
        const geohashes = rasterizeRoadPath(
          road.path,
          road.precision,
          road.widths
        );
        const widthSegments = buildWidthSegments(
          road.path,
          road.precision,
          road.widths
        );
        await haulRouteApi.createRoute({
          fromLocationName: road.fromLocationName,
          toLocationName: road.toLocationName,
          roadName: road.roadName,
          geohashes,
          minutes: road.minutes,
          routeVariant: null,
          truckCapacity: road.truckCapacity,
          weightCapacityInTon: road.weightCapacityInTon,
          distanceMeters: pathDistanceMeters(road.path),
          speedLimitMinKph: road.speedLimitMinKph,
          speedLimitMaxKph: road.speedLimitMaxKph,
          numberOfLanes: road.numberOfLanes,
          roadCondition: road.roadCondition,
          geoEngineerSuggestions: road.geoEngineerSuggestions,
          widthSegments,
          path: geoPointsToPathGeog(road.path),
        });
      })
    );
    const failed = pendingRoutes.filter(
      (_, idx) => results[idx].status === 'rejected'
    );
    const successCount = pendingRoutes.length - failed.length;
    setPendingRoutes(failed);
    setSavingAllRoutes(false);
    setPendingListAnchor(null);
    if (successCount > 0) {
      setShowRoutes(true);
      setHideRouteDistances(true);
      await fetchRoutes();
    }
    dispatch(
      showNotification({
        message:
          failed.length === 0
            ? t('roads_importer_save_all_success', { count: successCount })
            : t('roads_importer_save_all_partial', {
                saved: successCount,
                failed: failed.length,
              }),
        type: failed.length === 0 ? 'success' : 'error',
      })
    );
  }, [
    pendingRoutes,
    rasterizeRoadPath,
    buildWidthSegments,
    haulRouteApi,
    dispatch,
    t,
    fetchRoutes,
  ]);

  const removePendingRoute = useCallback((idx: number) => {
    setPendingRoutes((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clearPendingRoutes = useCallback(() => {
    setPendingRoutes([]);
    setPendingListAnchor(null);
  }, []);

  // Change a queued (not-yet-saved) road's width anytime before Save All
  const applyWidthEdit = useCallback(() => {
    const target = widthEditTarget;
    const width = parseFloat(widthEditValue);
    if (target == null || !isFinite(width) || width <= 0) return;
    setPendingRoutes((prev) =>
      prev.map((road, idx) =>
        idx === target
          ? { ...road, widths: road.widths.map(() => width) }
          : road
      )
    );
    setWidthEditTarget(null);
    dispatch(
      showNotification({
        message: t('roads_importer_width_updated', { width }),
        type: 'success',
      })
    );
  }, [widthEditTarget, widthEditValue, dispatch, t]);

  // ── Pending (queued, not-yet-saved) roads — amber, until Save All ──
  const pendingRouteGeoJson = useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (pendingRoutes.length === 0) return null;
    const features: GeoJSON.Feature[] = [];
    pendingRoutes.forEach((road) => {
      if (road.path.length < 2) return;
      groupSegmentsByWidth(road.path, [], ROAD_DISPLAY_WIDTH_METERS).forEach(
        (group) => {
          features.push({
            type: 'Feature',
            properties: { key: road.key },
            geometry: {
              type: 'LineString',
              coordinates: group.points.map((p) => [p.lon, p.lat]),
            },
          });
        }
      );
    });
    return features.length ? { type: 'FeatureCollection', features } : null;
  }, [pendingRoutes]);

  const pendingRouteLabels = useMemo(() => {
    return pendingRoutes
      .filter((road) => road.path.length >= 2)
      .map((road) => {
        const mid = road.path[Math.floor(road.path.length / 2)];
        return (
          <Marker
            key={`pending-road-${road.key}-label`}
            longitude={mid.lon}
            latitude={mid.lat}
            anchor="center"
          >
            <RoadDistanceLabel color={PENDING_ROUTE_COLOR} fontSize={8}>
              {formatDistance(pathDistanceMeters(road.path))}
            </RoadDistanceLabel>
          </Marker>
        );
      });
  }, [pendingRoutes]);

  // Live road length while drawing
  const draftDistanceMeters = useMemo(
    () => pathDistanceMeters(draftVertices),
    [draftVertices]
  );

  // When a location's boundary is redrawn/moved, its centroid moves — any
  // road that starts or ends there would otherwise keep pointing at the old
  // spot. This re-anchors just that one endpoint of each connected route's
  // path (leaving the rest of the drawn line untouched) and re-persists it.
  const stretchConnectedRoutes = useCallback(
    async (locationId: string, newCentroid: GeoPoint) => {
      const connected = routes.filter(
        (route) =>
          route.fromLocationId === locationId ||
          route.toLocationId === locationId
      );
      if (connected.length === 0) return;

      await Promise.allSettled(
        connected.map(async (route) => {
          const reconstructed = reconstructRoutePath(route);
          if (!reconstructed || reconstructed.path.length < 2) return;
          const { path, widths } = reconstructed;
          const newPath = [...path];
          if (route.fromLocationId === locationId) {
            newPath[0] = newCentroid;
          }
          if (route.toLocationId === locationId) {
            newPath[newPath.length - 1] = newCentroid;
          }

          const precision =
            route.geohashes?.[0]?.length ?? DEFAULT_GEOHASH_PRECISION;
          const geohashes = rasterizeRoadPath(newPath, precision, widths);
          const widthSegments = buildWidthSegments(newPath, precision, widths);
          const pathGeog = geoPointsToPathGeog(newPath);

          await Promise.all([
            haulRouteApi.updateGeohashes(route.routeId, geohashes, pathGeog),
            haulRouteApi.updateRoute(route.routeId, { widthSegments }),
          ]);
        })
      );
      await fetchRoutes();
    },
    [routes, haulRouteApi, rasterizeRoadPath, buildWidthSegments, fetchRoutes]
  );

  const saveGeohashes = useCallback(async (): Promise<boolean> => {
    if (draftVertices.length < 3) return false;
    const zoneGeog = geoPointsToZoneGeog(draftVertices);
    setSaving(true);
    try {
      if (editingLocation) {
        // Re-derive the centroid from the (possibly moved/redrawn) polygon —
        // otherwise editing a boundary leaves the old centroid behind,
        // which drifts the location's label/marker away from its own zone.
        const centroidLat =
          draftVertices.reduce((s, v) => s + v.lat, 0) / draftVertices.length;
        const centroidLon =
          draftVertices.reduce((s, v) => s + v.lon, 0) / draftVertices.length;
        await locationApi.updateLocation(editingLocation.locationId, {
          zoneGeog,
          centroidLat,
          centroidLon,
        });
        await stretchConnectedRoutes(editingLocation.locationId, {
          lat: centroidLat,
          lon: centroidLon,
        });
      } else if (creatingLocation) {
        const centroidLat =
          draftVertices.reduce((s, v) => s + v.lat, 0) / draftVertices.length;
        const centroidLon =
          draftVertices.reduce((s, v) => s + v.lon, 0) / draftVertices.length;
        const utm = latLonToUtm(
          centroidLat,
          centroidLon,
          proj4String,
          localOriginEasting,
          localOriginNorthing
        );
        const capacity = parseInt(creatingLocation.trucksCapacity, 10);
        const elevation = parseFloat(creatingLocation.elevation);
        const minutes = parseInt(creatingLocation.minutes, 10);
        const bulkResult = await locationApi.createLocations([
          {
            name: creatingLocation.name,
            locationType: creatingLocation.locationType,
            easting: utm?.easting ?? 0,
            northing: utm?.northing ?? 0,
            elevation: isFinite(elevation) ? elevation : 0,
            active: true,
            centroidLat,
            centroidLon,
            trucksCapacity: isFinite(capacity) ? capacity : null,
            minutes: isFinite(minutes) ? minutes : null,
          },
        ]);
        if (bulkResult.failed > 0) {
          const reason = bulkResult.failedLocations[0]?.reason;
          dispatch(
            showNotification({
              message: reason ?? t('roads_importer_save_failed'),
              type: 'error',
            })
          );
          return false;
        }
        // The bulk endpoint doesn't return the new id — find it by name
        // to attach the drawn boundary shape
        const fresh = await locationApi.getLocations();
        const created = fresh?.find(
          (l) => l.name?.toLowerCase() === creatingLocation.name?.toLowerCase()
        );
        if (created) {
          await locationApi.updateLocation(created.locationId, {
            zoneGeog,
          });
        }
      } else {
        return false;
      }
      dispatch(
        showNotification({
          message: t('roads_importer_save_success'),
          type: 'success',
        })
      );
      resetBoundaryDrag();
      setEditingLocation(null);
      setCreatingLocation(null);
      setDraftVertices([]);
      await fetchLocations();
      return true;
    } catch {
      dispatch(
        showNotification({
          message: t('roads_importer_save_failed'),
          type: 'error',
        })
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    editingLocation,
    creatingLocation,
    draftVertices,
    locationApi,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
    dispatch,
    t,
    fetchLocations,
    resetBoundaryDrag,
    stretchConnectedRoutes,
  ]);

  // Saves the moved/redrawn boundary, then — only once that succeeds —
  // clears locationId/locationName on every grade block confirmed as no
  // longer inside it. Grade ids are snapshotted up front since saveGeohashes
  // resets editingLocation/draftVertices on success, which would otherwise
  // collapse gradesToDeallocate to empty before this runs.
  const confirmUnmapAndSave = useCallback(async () => {
    const idsToClear = gradesToDeallocate.map((g) => g.id);
    setUnmapConfirmOpen(false);
    const saved = await saveGeohashes();
    if (!saved || idsToClear.length === 0) return;
    const idSet = new Set(idsToClear);
    await Promise.allSettled(
      grades
        .filter((grade) => idSet.has(grade.id))
        .map(async (grade) => {
          const { id, ...rest } = grade;
          await gradeApi.updateGrade(id, {
            ...rest,
            locationId: null,
            locationName: null,
          });
        })
    );
    await fetchGrades();
  }, [gradesToDeallocate, saveGeohashes, grades, gradeApi, fetchGrades]);

  const isDrawing =
    editingLocation != null ||
    creatingLocation != null ||
    drawingRoadFrom != null ||
    editingRouteTarget != null;

  // Same top-toolbar gate as isDrawing, plus "Move Centroid" mode — kept
  // separate from isDrawing itself since the bottom editing-controls bar
  // further down keys off isDrawing directly and has its own dedicated
  // Save/Cancel block for centroid moving instead.
  const hideTopControls = isDrawing || movingCentroidLocation != null;

  // Intersections are just a point on the map — boundary drawing and
  // per-vertex resizing don't apply, only repositioning the whole shape.
  const isIntersectionEdit =
    (
      editingLocation?.locationType ?? creatingLocation?.locationType
    )?.toLowerCase() === 'intersection';

  // Legend: only the location types actually present on the map
  const legendItems = useMemo<LegendItem[]>(() => {
    const fallbackHex = theme.palette.primary.main;
    const seen = new Map<string, string>();
    activeLocations.forEach((loc) => {
      const type = loc.locationType?.toLowerCase() ?? 'other';
      if (!seen.has(type)) {
        seen.set(type, zoneColorHex(loc.locationType, fallbackHex));
      }
    });
    const items: LegendItem[] = Array.from(seen.entries()).map(
      ([type, color]) => ({
        key: type,
        color,
        label: t(`roads_importer_type_${type}`, type),
      })
    );
    if (visibleLocations.some((loc) => !loc.active)) {
      items.push({
        key: 'inactive-locations',
        color: INACTIVE_ROUTE_COLOR,
        label: t('roads_importer_legend_inactive_locations'),
      });
    }
    if ((showRoutes || routeFilterLocation) && routes.some((r) => r.active)) {
      items.push({
        key: 'routes',
        color: ROUTE_COLOR,
        label: t('roads_importer_legend_routes'),
      });
    }
    if ((showRoutes || routeFilterLocation) && routes.some((r) => !r.active)) {
      items.push({
        key: 'inactive-routes',
        color: INACTIVE_ROUTE_COLOR,
        label: t('roads_importer_legend_inactive_routes'),
      });
    }
    if (pendingRoutes.length > 0) {
      items.push({
        key: 'pending-routes',
        color: PENDING_ROUTE_COLOR,
        label: t('roads_importer_legend_pending_routes'),
      });
    }
    if (
      (showRoutes || routeFilterLocation) &&
      routes.some((r) => blockedRoads.has(r.routeId))
    ) {
      items.push({
        key: 'blocked-routes',
        color: BLOCKED_ROUTE_COLOR,
        label: t('roads_importer_legend_blocked_routes'),
      });
    }
    if (customerCode === CAR_CUSTOMER_CODE && showRoadsOverlay) {
      items.push({
        key: 'almaly-roads',
        color: ALMALY_ROADS_COLOR,
        label: t('roads_importer_show_mine_map'),
      });
    }
    if (showGrades && gradesWithZone.length > 0) {
      // Only known categories get their own legend swatch — anything that
      // doesn't match one of them (missing/unrecognized) buckets under "Other".
      const seenCategories = new Set<string>();
      gradesWithZone.forEach(({ grade }) => {
        const cat = grade.gradeCategory?.toLowerCase()?.trim();
        seenCategories.add(cat && GRADE_CATEGORY_COLORS[cat] ? cat : 'other');
      });
      Array.from(seenCategories).forEach((cat, idx) => {
        items.push({
          key: `grade-category-${cat}`,
          color: GRADE_CATEGORY_COLORS[cat] ?? GRADE_ZONE_COLOR,
          label: t(`roads_importer_grade_category_${cat.replace(/\s+/g, '_')}`),
          section: 'grades',
          sectionLabel:
            idx === 0 ? t('roads_importer_grade_legend_title') : undefined,
        });
      });
    }
    return items;
  }, [
    activeLocations,
    visibleLocations,
    routes,
    pendingRoutes,
    blockedRoads,
    showRoutes,
    routeFilterLocation,
    customerCode,
    showRoadsOverlay,
    showGrades,
    gradesWithZone,
    theme.palette.primary.main,
    t,
  ]);

  const deleteRoutePair = useMemo(
    () =>
      deleteRouteTarget ? findReverseRoute(deleteRouteTarget, routes) : null,
    [deleteRouteTarget, routes]
  );

  const blockRouteOverlap = useMemo(
    () =>
      blockRouteTarget ? findOverlappingRoutes(blockRouteTarget, routes) : [],
    [blockRouteTarget, routes]
  );

  const unblockRouteOverlap = useMemo(
    () =>
      unblockRouteTarget
        ? findOverlappingRoutes(unblockRouteTarget, routes).filter((r) =>
            blockedRoads.has(r.routeId)
          )
        : [],
    [unblockRouteTarget, routes, blockedRoads]
  );

  // Right-click hit-testing (see the click/contextmenu handler above)
  // resolves to whichever route's geohashes happen to come first in
  // `routes` — a two-way road's forward and reverse legs occupy the exact
  // same cells, so the other direction was never reachable from the
  // context menu. Rather than changing that resolution (which every other
  // route action also relies on), "Edit Road" alone offers every
  // overlapping road by name so the user can pick which one to edit.
  const contextMenuRouteOverlap = useMemo(
    () =>
      contextMenu?.route
        ? findOverlappingRoutes(contextMenu.route, routes)
        : [],
    [contextMenu, routes]
  );

  const deleteRoute = useCallback(
    async (routeIds: string[]) => {
      if (routeIds.length === 0) return;
      setDeletingRoute(true);
      try {
        await Promise.all(
          routeIds.map(async (id) => {
            await haulRouteApi.deleteRoute(id);
          })
        );
        dispatch(
          showNotification({
            message: t('roads_importer_delete_route_success'),
            type: 'success',
          })
        );
        setDeleteRouteTarget(null);
        setRoutes((prev) => prev.filter((r) => !routeIds.includes(r.routeId)));
      } catch {
        dispatch(
          showNotification({
            message: t('roads_importer_delete_route_failed'),
            type: 'error',
          })
        );
      } finally {
        setDeletingRoute(false);
      }
    },
    [haulRouteApi, dispatch, t]
  );

  const openBlockRouteDialog = useCallback((route: HaulRoute) => {
    setBlockRouteTarget(route);
    setBlockRouteForm({ reason: '', ttl: { hours: '', minutes: '' } });
  }, []);

  const confirmBlockRoute = useCallback(
    async (blocks: BlockRoadSubmission[]) => {
      if (blocks.length === 0) return;
      // Reason is optional in the UI, but the API requires a non-empty
      // string — fall back to a placeholder rather than blocking the
      // action on it.
      const reason = blockRouteForm.reason.trim() || 'No Reason';
      setBlockingRoute(true);
      try {
        // allSettled (not all) so one road failing to block doesn't hide
        // that its overlapping/reverse leg still succeeded — a two-way road
        // left half-blocked with no indication which half is worse than a
        // clear "these specific roads failed" message.
        const results = await Promise.allSettled(
          blocks.map(async ({ routeId, ttlMinutes }) => {
            await dispatchMonitorApi.blockRoad(routeId, reason, ttlMinutes);
            return routeId;
          })
        );
        const failedIds = results
          .map((result, i) =>
            result.status === 'rejected' ? blocks[i].routeId : null
          )
          .filter((id): id is string => id !== null);
        await fetchBlockedRoads();
        setBlockRouteTarget(null);
        if (failedIds.length === 0) {
          dispatch(
            showNotification({
              message: t('roads_importer_block_route_success'),
              type: 'success',
            })
          );
        } else if (failedIds.length === blocks.length) {
          dispatch(
            showNotification({
              message: t('roads_importer_block_route_failed'),
              type: 'error',
            })
          );
        } else {
          const failedNames = failedIds
            .map((id) => {
              const failedRoute = routes.find((r) => r.routeId === id);
              return failedRoute
                ? formatRoadName(failedRoute.roadName ?? failedRoute.routeKey)
                : id;
            })
            .join(', ');
          dispatch(
            showNotification({
              message: t('roads_importer_block_route_partial_failed', {
                names: failedNames,
              }),
              type: 'error',
            })
          );
        }
      } finally {
        setBlockingRoute(false);
      }
    },
    [blockRouteForm, dispatchMonitorApi, dispatch, t, fetchBlockedRoads, routes]
  );

  const unblockRoutes = useCallback(
    async (routeIds: string[]) => {
      if (routeIds.length === 0) return;
      setUnblockingRoute(true);
      try {
        await Promise.all(
          routeIds.map(async (routeId) => {
            await dispatchMonitorApi.unblockRoad(routeId);
          })
        );
        dispatch(
          showNotification({
            message: t('roads_importer_unblock_route_success'),
            type: 'success',
          })
        );
        setUnblockRouteTarget(null);
        await fetchBlockedRoads();
      } catch {
        dispatch(
          showNotification({
            message: t('roads_importer_unblock_route_failed'),
            type: 'error',
          })
        );
      } finally {
        setUnblockingRoute(false);
      }
    },
    [dispatchMonitorApi, dispatch, t, fetchBlockedRoads]
  );

  // A one-way blocked road unblocks instantly (no dialog, matching the old
  // behavior) — the dialog only appears when there's at least one other
  // overlapping road that's also currently blocked, so the user can choose
  // whether to lift all of them together.
  const openUnblockRouteDialog = useCallback(
    (route: HaulRoute) => {
      const overlap = findOverlappingRoutes(route, routes).filter((r) =>
        blockedRoads.has(r.routeId)
      );
      if (overlap.length === 0) {
        void unblockRoutes([route.routeId]);
      } else {
        setUnblockRouteTarget(route);
      }
    },
    [routes, blockedRoads, unblockRoutes]
  );

  const toggleRouteActive = useCallback(
    async (route: HaulRoute) => {
      const nextActive = !route.active;
      try {
        await haulRouteApi.setRouteActive(route.routeId, nextActive);
        dispatch(
          showNotification({
            message: t(
              nextActive
                ? 'roads_importer_activate_route_success'
                : 'roads_importer_deactivate_route_success'
            ),
            type: 'success',
          })
        );
        await fetchRoutes();
      } catch {
        dispatch(
          showNotification({
            message: t(
              nextActive
                ? 'roads_importer_activate_route_failed'
                : 'roads_importer_deactivate_route_failed'
            ),
            type: 'error',
          })
        );
      }
    },
    [haulRouteApi, dispatch, t, fetchRoutes]
  );

  const startEditingRoute = useCallback((route: HaulRoute) => {
    setEditRouteTarget(route);
    const trimmedRoadName = route.roadName?.trim();
    const normalizedRoadName =
      trimmedRoadName === '' ? undefined : trimmedRoadName;
    setEditRouteRoadName(normalizedRoadName ?? formatRoadName(route.routeKey));
    setEditRouteMinutes(String(route.minutes ?? 5));
    setEditRouteCapacity(String(route.truckCapacity ?? 3));
    setEditRouteWeightCapacity(
      route.weightCapacityInTon != null ? String(route.weightCapacityInTon) : ''
    );
    setEditRouteSpeedLimitMin(String(route.speedLimitMinKph ?? 40));
    setEditRouteSpeedLimitMax(String(route.speedLimitMaxKph ?? 40));
    setEditRouteLanes(String(route.numberOfLanes ?? 2));
    setEditRouteCondition(route.roadCondition ?? 'good');
    setEditRouteSuggestions(route.geoEngineerSuggestions ?? '');
  }, []);

  const saveRouteEdit = useCallback(async () => {
    const route = editRouteTarget;
    if (!route) return;
    const minutes = parseInt(editRouteMinutes, 10);
    const capacity = parseInt(editRouteCapacity, 10);
    const weightCapacity = parseFloat(editRouteWeightCapacity);
    const speedLimitMin = parseFloat(editRouteSpeedLimitMin);
    const speedLimitMax = parseFloat(editRouteSpeedLimitMax);
    const lanes = parseInt(editRouteLanes, 10);
    const trimmedEditRouteRoadName = editRouteRoadName.trim() || undefined;
    setSavingRouteEdit(true);
    try {
      await haulRouteApi.updateRoute(route.routeId, {
        roadName: trimmedEditRouteRoadName ?? formatRoadName(route.routeKey),
        minutes: isFinite(minutes) ? minutes : 5,
        truckCapacity: isFinite(capacity) ? capacity : 3,
        weightCapacityInTon: isFinite(weightCapacity) ? weightCapacity : null,
        speedLimitMinKph: isFinite(speedLimitMin) ? speedLimitMin : 40,
        speedLimitMaxKph: isFinite(speedLimitMax) ? speedLimitMax : 40,
        numberOfLanes: isFinite(lanes) ? lanes : 1,
        roadCondition: editRouteCondition,
        geoEngineerSuggestions: editRouteSuggestions,
      });
      dispatch(
        showNotification({
          message: t('roads_importer_edit_route_success'),
          type: 'success',
        })
      );
      setEditRouteTarget(null);
      await fetchRoutes();
    } catch {
      dispatch(
        showNotification({
          message: t('roads_importer_edit_route_failed'),
          type: 'error',
        })
      );
    } finally {
      setSavingRouteEdit(false);
    }
  }, [
    editRouteTarget,
    editRouteRoadName,
    editRouteMinutes,
    editRouteCapacity,
    editRouteWeightCapacity,
    editRouteSpeedLimitMin,
    editRouteSpeedLimitMax,
    editRouteLanes,
    editRouteCondition,
    editRouteSuggestions,
    haulRouteApi,
    dispatch,
    t,
    fetchRoutes,
  ]);

  // Enter path-reshape mode for a saved route — reuses the same vertex-drag
  // mechanics as location boundary editing (see resetBoundaryDrag/isEditingRef),
  // just seeded with the route's reconstructed path instead of a polygon.
  const startEditingRoutePath = useCallback(
    (route: HaulRoute) => {
      const reconstructed = reconstructRoutePath(route);
      if (!reconstructed || reconstructed.path.length < 2) {
        dispatch(
          showNotification({
            message: t('roads_importer_edit_path_unavailable'),
            type: 'error',
          })
        );
        return;
      }
      resetBoundaryDrag();
      setEditingLocation(null);
      setCreatingLocation(null);
      setDrawingRoadFrom(null);
      setEditingRouteTarget(route);
      setEditingRouteFrom(
        locations.find((l) => l.locationId === route.fromLocationId) ?? null
      );
      setEditingRouteTo(
        locations.find((l) => l.locationId === route.toLocationId) ?? null
      );
      setDraftVertices(reconstructed.path);
      setBoundaryEditMode('vertex');
    },
    [resetBoundaryDrag, dispatch, t, locations]
  );

  const saveRoutePathEdit = useCallback(() => {
    const route = editingRouteTarget;
    if (!route || draftVertices.length < 2) return;

    const reconstructed = reconstructRoutePath(route);
    const widths = reconstructed?.widths ?? [];
    const precision = route.geohashes?.[0]?.length ?? DEFAULT_GEOHASH_PRECISION;
    const geohashes = rasterizeRoadPath(draftVertices, precision, widths);
    const widthSegments = buildWidthSegments(draftVertices, precision, widths);
    const pathGeog = geoPointsToPathGeog(draftVertices);
    const distanceMeters = pathDistanceMeters(draftVertices);
    const endpointPatch: Record<string, unknown> = {};
    if (
      editingRouteFrom &&
      editingRouteFrom.locationId !== route.fromLocationId
    ) {
      endpointPatch.fromLocationId = editingRouteFrom.locationId;
      endpointPatch.fromLocationName = editingRouteFrom.name;
    }
    if (editingRouteTo && editingRouteTo.locationId !== route.toLocationId) {
      endpointPatch.toLocationId = editingRouteTo.locationId;
      endpointPatch.toLocationName = editingRouteTo.name;
    }

    // A two-way road is stored as a mirrored pair of independent route
    // records (see queueRoad's `path: [...draftVertices].reverse()`) with
    // no field linking them — reshaping only `route` leaves its
    // overlapping/reverse leg's line behind on the map. Recompute geometry
    // for every route that physically overlays this one too, reversing
    // the path/widths for the reverse-direction leg.
    const overlapPatches = findOverlappingRoutes(route, routes).map(
      (overlap) => {
        const isReverse =
          overlap.fromLocationId === route.toLocationId &&
          overlap.toLocationId === route.fromLocationId;
        const overlapPath = isReverse
          ? [...draftVertices].reverse()
          : draftVertices;
        const overlapWidths = isReverse ? reverseRoadWidths(widths) : widths;
        const overlapPrecision = overlap.geohashes?.[0]?.length ?? precision;
        return {
          routeId: overlap.routeId,
          geohashes: rasterizeRoadPath(
            overlapPath,
            overlapPrecision,
            overlapWidths
          ),
          widthSegments: buildWidthSegments(
            overlapPath,
            overlapPrecision,
            overlapWidths
          ),
          pathGeog: geoPointsToPathGeog(overlapPath),
          distanceMeters,
        };
      }
    );

    // Apply the reshape to local state — and exit editing mode — immediately,
    // so the map reflects the new line right away instead of waiting on the
    // API round trip below. `previousRoutes` is the rollback target if any
    // of those calls fail.
    const previousRoutes = routes;
    setRoutes((prev) =>
      prev.map((r) => {
        if (r.routeId === route.routeId) {
          return {
            ...r,
            geohashes,
            widthSegments,
            pathGeog,
            distanceMeters,
            ...endpointPatch,
          };
        }
        const patch = overlapPatches.find((p) => p.routeId === r.routeId);
        return patch ? { ...r, ...patch } : r;
      })
    );
    cancelEditing();

    void (async () => {
      try {
        await Promise.all([
          haulRouteApi.updateGeohashes(route.routeId, geohashes, pathGeog),
          haulRouteApi.updateRoute(route.routeId, {
            widthSegments,
            ...endpointPatch,
          }),
          ...overlapPatches.flatMap((patch) => [
            haulRouteApi.updateGeohashes(
              patch.routeId,
              patch.geohashes,
              patch.pathGeog
            ),
            haulRouteApi.updateRoute(patch.routeId, {
              widthSegments: patch.widthSegments,
            }),
          ]),
        ]);
        dispatch(
          showNotification({
            message: t('roads_importer_edit_path_success'),
            type: 'success',
          })
        );
      } catch {
        setRoutes(previousRoutes);
        dispatch(
          showNotification({
            message: t('roads_importer_edit_path_failed'),
            type: 'error',
          })
        );
      }
    })();
  }, [
    editingRouteTarget,
    editingRouteFrom,
    editingRouteTo,
    draftVertices,
    routes,
    rasterizeRoadPath,
    buildWidthSegments,
    haulRouteApi,
    dispatch,
    t,
    cancelEditing,
  ]);

  const deleteNode = useCallback(async () => {
    const loc = deleteTarget;
    if (!loc) return;
    setDeleting(true);
    try {
      await locationApi.deleteLocation(loc.locationId);
      dispatch(
        showNotification({
          message: t('roads_importer_delete_success'),
          type: 'success',
        })
      );
      setDeleteTarget(null);
      await fetchLocations();
    } catch {
      dispatch(
        showNotification({
          message: t('roads_importer_delete_failed'),
          type: 'error',
        })
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, locationApi, dispatch, t, fetchLocations]);

  // ── Draft polygon / road path (draw modes) ──
  const draftRender = useMemo(() => {
    if (
      !editingLocation &&
      !creatingLocation &&
      !drawingRoadFrom &&
      !editingRouteTarget
    )
      return null;
    const isRoad = drawingRoadFrom != null || editingRouteTarget != null;
    const primaryColor = theme.palette.primary.main;
    const endpointColor = ROUTE_COLOR;

    const polygonGeoJson: GeoJSON.FeatureCollection | null =
      !isRoad && draftVertices.length >= 3
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Polygon',
                  coordinates: [closeRing(draftVertices)],
                },
              },
            ],
          }
        : null;

    const roadCorridorGeoJson: GeoJSON.FeatureCollection | null =
      isRoad && draftVertices.length >= 2
        ? {
            type: 'FeatureCollection',
            features: groupSegmentsByWidth(
              draftVertices,
              [],
              ROAD_DISPLAY_WIDTH_METERS
            ).map((group) => ({
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: group.points.map((p) => [p.lon, p.lat]),
              },
            })),
          }
        : null;

    const pathGeoJson: GeoJSON.FeatureCollection | null =
      draftVertices.length >= 2
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates:
                    !isRoad && draftVertices.length >= 3
                      ? closeRing(draftVertices)
                      : draftVertices.map((p) => [p.lon, p.lat]),
                },
              },
            ],
          }
        : null;

    const vertexMarkers = draftVertices.map((v, idx) => {
      // Route path editing: endpoints are draggable too (dropping one
      // on another location reconnects the road there) — styled
      // distinctly from interior joints as a "connection point" hint.
      const isRouteEndpoint =
        editingRouteTarget != null &&
        (idx === 0 || idx === draftVertices.length - 1);
      return (
        <Marker
          key={`draft-vertex-${idx}`}
          longitude={v.lon}
          latitude={v.lat}
          anchor="center"
        >
          <Box
            sx={{
              width: isRouteEndpoint ? 15 : 13,
              height: isRouteEndpoint ? 15 : 13,
              borderRadius: '50%',
              bgcolor: isRouteEndpoint ? endpointColor : primaryColor,
              border: '2px solid #fff',
              pointerEvents: 'none',
            }}
          />
        </Marker>
      );
    });

    return {
      polygonGeoJson,
      roadCorridorGeoJson,
      pathGeoJson,
      primaryColor,
      vertexMarkers,
    };
  }, [
    editingLocation,
    creatingLocation,
    drawingRoadFrom,
    editingRouteTarget,
    draftVertices,
    theme.palette.primary.main,
  ]);

  // ── Haul route corridors ──
  // Routes saved with widthSegments carry the exact drawn path + per-segment
  // width, so they render as a true smooth line — identical to how the road
  // looked while drawing. Older routes without widthSegments only have
  // rasterized geohash cells, so they fall back to a dissolved, smoothed
  // cell-boundary polygon (better than one rectangle per cell, but still an
  // approximation of the original line).
  const routeRender = useMemo(() => {
    // "Move Centroid" mode isolates the map to just the one location being
    // repositioned — every road is hidden outright while it's active.
    if (movingCentroidLocation != null) return null;
    // A selected Find Route result isolates the map the same way — showing
    // every other saved road alongside the highlighted one just reads as
    // clutter, not context.
    if (foundRoutePath) return null;
    if (visibleRoutes.length === 0) return null;

    const lineFeatures: GeoJSON.Feature[] = [];
    const polygonFeatures: GeoJSON.Feature[] = [];
    const labels: JSX.Element[] = [];

    visibleRoutes.forEach((route) => {
      const isBlocked = blockedRoads.has(route.routeId);
      const routeColor = isBlocked
        ? BLOCKED_ROUTE_COLOR
        : route.active
        ? ROUTE_COLOR
        : INACTIVE_ROUTE_COLOR;
      const fillAlphaValue = isDrawing
        ? 0.2
        : isBlocked
        ? 0.6
        : route.active
        ? 0.5
        : 0.35;
      const color = alpha(routeColor, fillAlphaValue);
      const reconstructed = reconstructRoutePath(route);

      if (reconstructed) {
        let { path } = reconstructed;
        // While the connected location's shape is being drag-moved, shift
        // this route's endpoint live so it visually follows along —
        // otherwise the road looks disconnected until Save re-stretches it.
        if (editingLocation && shapeDragDelta && path.length >= 2) {
          const shift = (p: GeoPoint): GeoPoint => ({
            lat: p.lat + shapeDragDelta.lat,
            lon: p.lon + shapeDragDelta.lon,
          });
          if (route.fromLocationId === editingLocation.locationId) {
            path = [shift(path[0]), ...path.slice(1)];
          }
          if (route.toLocationId === editingLocation.locationId) {
            path = [...path.slice(0, -1), shift(path[path.length - 1])];
          }
        }
        groupSegmentsByWidth(path, [], ROAD_DISPLAY_WIDTH_METERS).forEach(
          (group) => {
            lineFeatures.push({
              type: 'Feature',
              properties: { color },
              geometry: {
                type: 'LineString',
                coordinates: group.points.map((p) => [p.lon, p.lat]),
              },
            });
          }
        );
        const mid = path[Math.floor(path.length / 2)];
        if (route.distanceMeters != null && !isDrawing && !hideRouteDistances) {
          labels.push(
            <Marker
              key={`route-${route.routeId}-label`}
              longitude={mid.lon}
              latitude={mid.lat}
              anchor="center"
            >
              <RoadDistanceLabel color={routeColor} fontSize={10}>
                {formatDistance(route.distanceMeters)}
              </RoadDistanceLabel>
            </Marker>
          );
        }
        return;
      }

      const rings = geohashUnionRings(route.geohashes ?? []);
      if (rings.length === 0) return;
      const [outer, ...holes] = rings;
      const smoothOuter = chaikinSmooth(outer);
      polygonFeatures.push({
        type: 'Feature',
        properties: { color },
        geometry: {
          type: 'Polygon',
          coordinates: [
            closeRing(smoothOuter),
            ...holes.map((hole) => closeRing(chaikinSmooth(hole))),
          ],
        },
      });
      const centroidLat = outer.reduce((s, p) => s + p.lat, 0) / outer.length;
      const centroidLon = outer.reduce((s, p) => s + p.lon, 0) / outer.length;
      if (route.distanceMeters != null && !isDrawing && !hideRouteDistances) {
        labels.push(
          <Marker
            key={`route-${route.routeId}-label`}
            longitude={centroidLon}
            latitude={centroidLat}
            anchor="center"
          >
            <RoadDistanceLabel color={routeColor} fontSize={10}>
              {formatDistance(route.distanceMeters)}
            </RoadDistanceLabel>
          </Marker>
        );
      }
    });

    const linesGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: lineFeatures,
    };
    const polygonsGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: polygonFeatures,
    };
    return {
      lines: lineFeatures.length > 0 ? linesGeoJson : null,
      polygons: polygonFeatures.length > 0 ? polygonsGeoJson : null,
      labels,
    };
  }, [
    visibleRoutes,
    isDrawing,
    hideRouteDistances,
    editingLocation,
    shapeDragDelta,
    blockedRoads,
    movingCentroidLocation,
    foundRoutePath,
  ]);

  const locationRender = useMemo(() => {
    const fallbackHex = theme.palette.primary.main;
    // "Move Centroid" mode isolates the map to just this one location —
    // every other location is hidden outright while it's active. A selected
    // Find Route result does the same for the stops on that route.
    const locationsToRender = movingCentroidLocation
      ? [movingCentroidLocation]
      : foundRouteLocationIds
      ? visibleLocations.filter((loc) =>
          foundRouteLocationIds.has(loc.locationId)
        )
      : visibleLocations;

    const zoneFeatures: GeoJSON.Feature[] = [];
    const labels: JSX.Element[] = [];

    locationsToRender.forEach((loc) => {
      const isEditingThis = editingLocation?.locationId === loc.locationId;
      const dimmed = editingLocation != null && !isEditingThis;
      const inactive = !loc.active;
      const colorHex = inactive
        ? INACTIVE_ROUTE_COLOR
        : zoneColorHex(loc.locationType, fallbackHex);
      const fillAlphaValue = dimmed ? 0.12 : isEditingThis ? 0.15 : 0.35;
      const outlineAlphaValue = dimmed ? 0.3 : isEditingThis ? 0.5 : 1;
      const fillColor = alpha(
        colorHex,
        inactive ? fillAlphaValue * 0.6 : fillAlphaValue
      );
      const outlineColor = alpha(
        colorHex,
        inactive ? outlineAlphaValue * 0.6 : outlineAlphaValue
      );
      const hull = locationRing(loc);
      if (hull.length >= 3) {
        zoneFeatures.push({
          type: 'Feature',
          properties: { fillColor, outlineColor },
          geometry: { type: 'Polygon', coordinates: [closeRing(hull)] },
        });
      }

      // Anchor the label to the same center used for road-destination
      // hit-testing (hull center when available, else the stored centroid).
      const center = locationCenterPoint(loc);
      if (center && !dimmed) {
        labels.push(
          <Marker
            key={`label-${loc.locationId}`}
            longitude={center.lon}
            latitude={center.lat}
            anchor="center"
          >
            <LocationNameLabel dimmed={inactive}>{loc.name}</LocationNameLabel>
          </Marker>
        );
      }
    });

    const zonesGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: zoneFeatures,
    };
    return {
      zones: zoneFeatures.length > 0 ? zonesGeoJson : null,
      labels,
    };
  }, [
    visibleLocations,
    editingLocation,
    theme.palette.primary.main,
    movingCentroidLocation,
    foundRouteLocationIds,
  ]);

  // ── "Move Centroid" draggable marker ──
  // Not memoized against a stable dependency list on purpose — centroidDraft
  // changes on every mousemove frame while dragging, and this needs to
  // follow it live, the same way draftRender re-renders live off
  // draftVertices during boundary vertex dragging.
  const centroidMarkerElement = useMemo(() => {
    if (!movingCentroidLocation || !centroidDraft) return null;
    return (
      <Marker
        longitude={centroidDraft.lon}
        latitude={centroidDraft.lat}
        anchor="bottom"
      >
        <Box
          component="img"
          src={locationPinIconDataUri(theme.palette.primary.main)}
          sx={{
            width: CENTROID_PIN_ICON_PX,
            height: CENTROID_PIN_ICON_PX,
            pointerEvents: 'none',
          }}
        />
      </Marker>
    );
  }, [movingCentroidLocation, centroidDraft, theme.palette.primary.main]);

  // ── Grade zones (material grade boundaries drawn in Grade Importer) ──
  const gradeGeoJson = useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (!showGrades || gradesWithZone.length === 0) return null;
    const fallbackColor = GRADE_CATEGORY_COLORS.other ?? GRADE_ZONE_COLOR;
    const features: GeoJSON.Feature[] = gradesWithZone.map(
      ({ grade, ring }) => {
        const colorHex = gradeCategoryColorHex(
          grade.gradeCategory,
          fallbackColor
        );
        return {
          type: 'Feature',
          properties: {
            fillColor: alpha(colorHex, 0.12),
            outlineColor: alpha(colorHex, 0.45),
          },
          geometry: { type: 'Polygon', coordinates: [closeRing(ring)] },
        };
      }
    );
    return { type: 'FeatureCollection', features };
  }, [showGrades, gradesWithZone]);

  const headerHint =
    movingCentroidLocation != null
      ? t('roads_importer_move_centroid_hint', {
          name: movingCentroidLocation.name,
        })
      : drawingRoadFrom != null
      ? t('roads_importer_draw_road_hint', {
          name: drawingRoadFrom.name,
        })
      : editingRouteTarget != null
      ? t('roads_importer_edit_path_hint')
      : isDrawing
      ? boundaryEditMode === 'vertex'
        ? t('boundary_map_drag_hint')
        : boundaryEditMode === 'shape'
        ? t('roads_importer_shape_drag_hint')
        : t('roads_importer_edit_hint')
      : null;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 115px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pb: 2,
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pt: 1,
          flexWrap: 'wrap',
        }}
      >
        <AddRoadIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.2 }}
            >
              {t('roads_importer_title')}
            </Typography>
            <Tooltip title={t('roads_importer_faq_tooltip')}>
              <IconButton
                size="small"
                aria-label={t('roads_importer_faq_tooltip')}
                onClick={() => {
                  setFaqOpen(true);
                }}
                sx={{ color: 'text.secondary' }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {headerHint != null && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {headerHint}
            </Typography>
          )}
        </Box>

        {!hideTopControls && (
          <Autocomplete
            size="small"
            options={activeLocations}
            getOptionLabel={(loc) => loc.name}
            value={null}
            onChange={(_, loc) => {
              if (loc?.centroidLat != null && loc.centroidLon != null) {
                flyTo(loc.centroidLat, loc.centroidLon, 17, 1500);
              }
            }}
            isOptionEqualToValue={(a, b) => a.locationId === b.locationId}
            sx={{ width: 220 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('roads_importer_search_location')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <SearchIcon
                      sx={{ fontSize: 18, color: 'text.secondary', ml: 0.5 }}
                    />
                  ),
                }}
              />
            )}
          />
        )}

        {!hideTopControls && (
          <Button
            variant={findRouteOpen ? 'contained' : 'outlined'}
            size="small"
            startIcon={<AltRouteIcon fontSize="small" />}
            onClick={() => {
              if (findRouteOpen) {
                closeFindRoute();
              } else {
                setFindRouteOpen(true);
              }
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('roads_importer_find_route_toggle')}
          </Button>
        )}

        {!hideTopControls && (
          <>
            <input
              ref={geoJsonFileInputRef}
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              style={{ display: 'none' }}
              onChange={handleGeoJsonFileChange}
            />
            <Tooltip title={t('roads_importer_import_geojson_tooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon fontSize="small" />}
                onClick={() => geoJsonFileInputRef.current?.click()}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('roads_importer_import_geojson')}
              </Button>
            </Tooltip>
            {importedGeoJsonName && (
              <Chip
                size="small"
                variant="outlined"
                label={importedGeoJsonName}
                onDelete={handleClearImportedGeoJson}
                sx={{ fontWeight: 600 }}
              />
            )}
          </>
        )}

        {!hideTopControls && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showRoutes}
                onChange={(e) => {
                  setShowRoutes(e.target.checked);
                  if (e.target.checked) {
                    setRouteFilterLocation(null);
                    setHideRouteDistances(true);
                  }
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('roads_importer_show_routes')}
              </Typography>
            }
          />
        )}

        {!hideTopControls && showRoutes && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hideRouteDistances}
                onChange={(e) => {
                  setHideRouteDistances(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('roads_importer_hide_distances')}
              </Typography>
            }
          />
        )}

        {!hideTopControls && customerCode === CAR_CUSTOMER_CODE && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showRoadsOverlay}
                onChange={(e) => {
                  setShowRoadsOverlay(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('roads_importer_show_mine_map')}
              </Typography>
            }
          />
        )}

        {!hideTopControls && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showAssets}
                onChange={(e) => {
                  setShowAssets(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('roads_importer_show_assets')}
              </Typography>
            }
          />
        )}

        {!hideTopControls && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showGrades}
                onChange={(e) => {
                  setShowGrades(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('roads_importer_show_grades')}
              </Typography>
            }
          />
        )}

        {!hideTopControls && pendingRoutes.length > 0 && (
          <>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('roads_importer_pending_chip', {
                count: pendingRoutes.length,
              })}
              onClick={(e) => {
                setPendingListAnchor(e.currentTarget);
              }}
              sx={{ fontWeight: 600 }}
            />
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={
                savingAllRoutes ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CheckIcon />
                )
              }
              disabled={savingAllRoutes}
              onClick={() => {
                void saveAllRoutes();
              }}
              sx={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {t('roads_importer_btn_save_all', {
                count: pendingRoutes.length,
              })}
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={savingAllRoutes}
              onClick={clearPendingRoutes}
              sx={{ fontWeight: 600 }}
            >
              {t('roads_importer_btn_clear_all')}
            </Button>
          </>
        )}

        {creatingLocation && (
          <Chip
            size="small"
            color="primary"
            label={creatingLocation.name}
            sx={{ fontWeight: 600 }}
          />
        )}
        {drawingRoadFrom && (
          <>
            <Chip
              size="small"
              color="primary"
              label={drawingRoadFrom.name}
              sx={{ fontWeight: 600 }}
            />
            {draftVertices.length >= 2 && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={formatDistance(draftDistanceMeters)}
                sx={{ fontWeight: 600 }}
              />
            )}
          </>
        )}
        {isDrawing && (
          <>
            {!drawingRoadFrom && !editingRouteTarget && (
              <ToggleButtonGroup
                value={boundaryEditMode}
                exclusive
                size="small"
                onChange={(_, val: BoundaryEditMode | null) => {
                  if (val !== null) setBoundaryEditMode(val);
                }}
                sx={{ height: 32 }}
              >
                {!isIntersectionEdit && (
                  <ToggleButton value="draw" sx={{ fontWeight: 600, px: 1.2 }}>
                    <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    {t('boundary_map_mode_draw')}
                  </ToggleButton>
                )}
                {!isIntersectionEdit && (
                  <ToggleButton
                    value="vertex"
                    sx={{ fontWeight: 600, px: 1.2 }}
                  >
                    <OpenWithIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    {t('boundary_map_mode_drag')}
                  </ToggleButton>
                )}
                <ToggleButton value="shape" sx={{ fontWeight: 600, px: 1.2 }}>
                  <DragIndicatorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {t('roads_importer_mode_move_shape')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {!editingRouteTarget && (
              <Chip
                size="small"
                label={`${draftVertices.length} ${t('roads_importer_points')}`}
                sx={{ fontWeight: 600 }}
              />
            )}
            {!editingRouteTarget && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UndoIcon />}
                disabled={saving || draftVertices.length === 0}
                onClick={() => {
                  setDraftVertices((prev) => prev.slice(0, -1));
                  if (drawingRoadFrom) {
                    setRoadVertexWidths((prev) => prev.slice(0, -1));
                  }
                }}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('roads_importer_btn_undo')}
              </Button>
            )}
            {!drawingRoadFrom && !editingRouteTarget && (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  saving ? <CircularProgress size={14} /> : <CheckIcon />
                }
                disabled={saving || draftVertices.length < 3}
                onClick={() => {
                  if (gradesToDeallocate.length > 0) {
                    setUnmapConfirmOpen(true);
                  } else {
                    void saveGeohashes();
                  }
                }}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('roads_importer_btn_save')}
              </Button>
            )}
            {editingRouteTarget && (
              <Button
                variant="contained"
                size="small"
                startIcon={<CheckIcon />}
                disabled={draftVertices.length < 2}
                onClick={() => {
                  saveRoutePathEdit();
                }}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('roads_importer_btn_save')}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon />}
              disabled={saving}
              onClick={cancelEditing}
              sx={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {t('roads_importer_btn_cancel')}
            </Button>
          </>
        )}

        {movingCentroidLocation && (
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={
                savingCentroid ? <CircularProgress size={14} /> : <CheckIcon />
              }
              disabled={savingCentroid}
              onClick={() => {
                void saveCentroid();
              }}
              sx={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {t('roads_importer_btn_save')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon />}
              disabled={savingCentroid}
              onClick={cancelEditing}
              sx={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {t('roads_importer_btn_cancel')}
            </Button>
          </>
        )}
      </Box>

      {/* ── Site Map ── */}
      <Box
        onContextMenu={(e) => {
          // Belt-and-suspenders: MapLibre's own 'contextmenu' map event
          // (see the map-interactions effect) already opens our menu and
          // calls preventDefault. This catches the rare case of a
          // right-click landing on an overlay (loading spinner, drawing
          // banner) that sits on top of the map canvas.
          e.preventDefault();
        }}
        sx={{
          flex: 1,
          minHeight: 480,
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: (th) =>
            isDrawing
              ? alpha(th.palette.primary.main, 0.5)
              : alpha(th.palette.divider, 0.5),
          cursor:
            boundaryEditMode !== 'draw'
              ? isDraggingVertex
                ? 'grabbing'
                : 'grab'
              : isDrawing
              ? 'crosshair'
              : 'default',
          transition: 'border-color 0.2s',
        }}
      >
        {!mapReady && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              zIndex: 5,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {/* ── Drawing instructions banner — only makes sense while actually
            clicking points to draw a location boundary; vertex-drag/move-
            shape modes and road drawing/reshaping have their own hint in the
            header ── */}
        {(creatingLocation != null || editingLocation != null) &&
          boundaryEditMode === 'draw' && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'primary.main',
                boxShadow: 3,
                maxWidth: '90%',
              }}
            >
              <TouchAppOutlinedIcon
                fontSize="small"
                sx={{
                  color: 'primary.main',
                  animation: 'roadsImporterPulse 1.4s ease-in-out infinite',
                  '@keyframes roadsImporterPulse': {
                    '0%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.25)', opacity: 0.6 },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                {draftVertices.length < 3
                  ? t('roads_importer_draw_banner', {
                      name:
                        creatingLocation?.name ?? editingLocation?.name ?? '',
                    })
                  : t('roads_importer_draw_banner_done')}
              </Typography>
            </Box>
          )}

        <MapView
          ref={mapRef}
          onLoad={handleMapLoad}
          maxBounds={maxBoundsProp}
          maxZoom={MINE_MAP_MAX_ZOOM}
          style={{ position: 'absolute', inset: 0 }}
        >
          {gradeGeoJson && (
            <Source id="grade-zones" type="geojson" data={gradeGeoJson}>
              <Layer
                id="grade-zones-fill"
                type="fill"
                paint={{ 'fill-color': ['get', 'fillColor'] }}
              />
              <Layer
                id="grade-zones-outline"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': ['get', 'outlineColor'],
                  'line-width': 1.5,
                }}
              />
            </Source>
          )}

          {routeRender?.polygons && (
            <Source
              id="route-polygons"
              type="geojson"
              data={routeRender.polygons}
            >
              <Layer
                id="route-polygons-fill"
                type="fill"
                paint={{ 'fill-color': ['get', 'color'] }}
              />
            </Source>
          )}

          {routeRender?.lines && (
            <Source id="route-lines" type="geojson" data={routeRender.lines}>
              <Layer
                id="route-lines-layer"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ROAD_LINE_WIDTH_EXPRESSION,
                }}
              />
            </Source>
          )}

          {foundRouteGeoJson && (
            <Source id="found-route" type="geojson" data={foundRouteGeoJson}>
              <Layer
                id="found-route-layer"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': alpha(FOUND_ROUTE_COLOR, 0.9),
                  'line-width': ROAD_LINE_WIDTH_EXPRESSION,
                }}
              />
            </Source>
          )}

          {pendingRouteGeoJson && (
            <Source
              id="pending-routes"
              type="geojson"
              data={pendingRouteGeoJson}
            >
              <Layer
                id="pending-routes-layer"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': alpha(PENDING_ROUTE_COLOR, 0.55),
                  'line-width': ROAD_LINE_WIDTH_EXPRESSION,
                }}
              />
            </Source>
          )}

          {locationRender.zones && (
            <Source
              id="location-zones"
              type="geojson"
              data={locationRender.zones}
            >
              <Layer
                id="location-zones-fill"
                type="fill"
                paint={{ 'fill-color': ['get', 'fillColor'] }}
              />
              <Layer
                id="location-zones-outline"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': ['get', 'outlineColor'],
                  'line-width': 3,
                }}
              />
            </Source>
          )}

          {draftRender?.polygonGeoJson && (
            <Source
              id="draft-polygon"
              type="geojson"
              data={draftRender.polygonGeoJson}
            >
              <Layer
                id="draft-polygon-fill"
                type="fill"
                paint={{ 'fill-color': alpha(draftRender.primaryColor, 0.2) }}
              />
            </Source>
          )}

          {draftRender?.roadCorridorGeoJson && (
            <Source
              id="draft-road-corridor"
              type="geojson"
              data={draftRender.roadCorridorGeoJson}
            >
              <Layer
                id="draft-road-corridor-layer"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': alpha(draftRender.primaryColor, 0.45),
                  'line-width': ROAD_LINE_WIDTH_EXPRESSION,
                }}
              />
            </Source>
          )}

          {draftRender?.pathGeoJson && (
            <Source
              id="draft-path"
              type="geojson"
              data={draftRender.pathGeoJson}
            >
              <Layer
                id="draft-path-layer"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': alpha(draftRender.primaryColor, 0.9),
                  'line-width': 2.5,
                }}
              />
            </Source>
          )}

          {importedGeoJson && (
            <Source
              id="imported-geojson"
              type="geojson"
              data={importedGeoJson as GeoJSON.FeatureCollection}
            >
              <Layer
                id="imported-geojson-fill"
                type="fill"
                filter={IMPORTED_GEOJSON_POLYGON_FILTER}
                paint={{
                  'fill-color': IMPORTED_GEOJSON_COLOR,
                  'fill-opacity': 0.2,
                }}
              />
              <Layer
                id="imported-geojson-line"
                type="line"
                filter={IMPORTED_GEOJSON_LINE_FILTER}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{
                  'line-color': IMPORTED_GEOJSON_COLOR,
                  'line-width': 2.5,
                }}
              />
              <Layer
                id="imported-geojson-point"
                type="circle"
                filter={IMPORTED_GEOJSON_POINT_FILTER}
                paint={{
                  'circle-color': IMPORTED_GEOJSON_COLOR,
                  'circle-radius': 5,
                }}
              />
            </Source>
          )}

          {routeRender?.labels}
          {locationRender.labels}
          {pendingRouteLabels}
          {draftRender?.vertexMarkers}
          {centroidMarkerElement}
          {showAssets && truckMarkers}
        </MapView>

        {/* ── Satellite/neutral-road basemap toggle ── */}
        {mapReady && (
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 6 }}>
            <ToggleButtonGroup
              size="small"
              value={mapBasemap}
              exclusive
              onChange={(_event, value: BasemapId | null) => {
                if (value) handleBasemapToggle(value);
              }}
              sx={{ backgroundColor: 'background.paper' }}
            >
              <ToggleButton value="neutralRoad" aria-label={t('map_view')}>
                <MapIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="satellite" aria-label={t('satellite_view')}>
                <SatelliteAltIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* ── Active route filter (click × to reset) ── */}
        {!hideTopControls && routeFilterLocation && (
          <Chip
            color="primary"
            label={t('roads_importer_routes_from_chip', {
              name: routeFilterLocation.name,
            })}
            onDelete={() => {
              setRouteFilterLocation(null);
            }}
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 6,
              fontWeight: 600,
              boxShadow: 3,
            }}
          />
        )}

        {/* ── Legend ── */}
        {mapReady && <RoadsImporterLegend items={legendItems} />}

        {/* ── Find Route panel ── */}
        {findRouteOpen && (
          <FindRoutePanel
            locations={activeLocations}
            from={findRouteFrom}
            to={findRouteTo}
            via={findRouteVia}
            onChangeFrom={(loc) => {
              setFindRouteFrom(loc);
              resetFindRouteResults();
            }}
            onChangeTo={(loc) => {
              setFindRouteTo(loc);
              resetFindRouteResults();
            }}
            onChangeVia={(locs) => {
              setFindRouteVia(locs);
              resetFindRouteResults();
            }}
            onFind={() => {
              void handleFindRoutes();
            }}
            onClose={closeFindRoute}
            loading={findRouteLoading}
            results={findRouteResults}
            selectedIndex={findRouteSelectedIndex}
            onSelect={setFindRouteSelectedIndex}
          />
        )}
      </Box>

      <RoadsImporterContextMenu
        contextMenu={contextMenu}
        overlappingRoutes={contextMenuRouteOverlap}
        onClose={() => {
          setContextMenu(null);
        }}
        onEditNode={startEditing}
        onMoveCentroid={startMovingCentroid}
        onRoutesFromNode={(loc) => {
          setRouteFilterLocation(loc);
          setShowRoutes(false);
        }}
        onDrawRoadFrom={(loc) => {
          if (loc) startDrawingRoad(loc);
        }}
        onDeleteNode={setDeleteTarget}
        onChangePendingWidth={(idx) => {
          const widths = pendingRoutes[idx]?.widths ?? [];
          setWidthEditValue(String(widths[widths.length - 1] ?? 50));
          setWidthEditTarget(idx);
        }}
        onViewRoute={setViewRouteTarget}
        onEditRoute={startEditingRoute}
        onEditRoutePath={startEditingRoutePath}
        onToggleRouteActive={(route) => {
          void toggleRouteActive(route);
        }}
        onBlockRoute={openBlockRouteDialog}
        onUnblockRoute={openUnblockRouteDialog}
        onDeleteRoute={setDeleteRouteTarget}
        onAddLocation={() => {
          setNewLocation(EMPTY_NEW_LOCATION);
          setAddDialogOpen(true);
        }}
        onMapGradeBlocks={setMapGradeBlocksTarget}
        blockedRoads={blockedRoads}
      />

      <BlockRoadDialog
        target={blockRouteTarget}
        overlappingRoutes={blockRouteOverlap}
        value={blockRouteForm}
        onChange={setBlockRouteForm}
        onClose={() => {
          setBlockRouteTarget(null);
        }}
        onSave={(blocks) => {
          void confirmBlockRoute(blocks);
        }}
        saving={blockingRoute}
      />

      <UnblockRoadDialog
        target={unblockRouteTarget}
        overlappingBlockedRoutes={unblockRouteOverlap}
        unblocking={unblockingRoute}
        onClose={() => {
          setUnblockRouteTarget(null);
        }}
        onConfirm={(routeIds) => {
          void unblockRoutes(routeIds);
        }}
      />

      <RoadsImporterFaqDialog
        open={faqOpen}
        onClose={() => {
          setFaqOpen(false);
        }}
      />

      <PendingRoutesPopover
        anchorEl={pendingListAnchor}
        pendingRoutes={pendingRoutes}
        onClose={() => {
          setPendingListAnchor(null);
        }}
        onRemove={removePendingRoute}
      />

      <GradeDetailsPopover
        target={gradeDetailsTarget}
        onClose={() => {
          setGradeDetailsTarget(null);
        }}
      />

      <RoadHoverTooltip target={contextMenu ? null : hoveredRoutesTarget} />

      <MapGradeBlocksDialog
        location={mapGradeBlocksTarget}
        grades={mapGradeBlocksCandidates}
        onClose={() => {
          setMapGradeBlocksTarget(null);
        }}
        onConfirm={confirmMapGradeBlocks}
      />

      <UnmapGradeBlocksDialog
        open={unmapConfirmOpen}
        location={editingLocation}
        grades={gradesToDeallocate}
        onCancel={() => {
          setUnmapConfirmOpen(false);
        }}
        onConfirm={confirmUnmapAndSave}
      />

      <AddLocationDialog
        open={addDialogOpen}
        value={newLocation}
        existingNames={locationNames}
        onChange={setNewLocation}
        onClose={() => {
          setAddDialogOpen(false);
        }}
        onContinue={(draft) => {
          setAddDialogOpen(false);
          startCreating(draft);
        }}
      />

      <DeleteNodeDialog
        target={deleteTarget}
        deleting={deleting}
        onClose={() => {
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          void deleteNode();
        }}
      />

      <DeleteRouteDialog
        target={deleteRouteTarget}
        pairedRoute={deleteRoutePair}
        deleting={deletingRoute}
        onClose={() => {
          setDeleteRouteTarget(null);
        }}
        onConfirm={(routeIds) => {
          void deleteRoute(routeIds);
        }}
      />

      <ViewRouteDialog
        target={viewRouteTarget}
        onClose={() => {
          setViewRouteTarget(null);
        }}
        onEdit={(route) => {
          setViewRouteTarget(null);
          startEditingRoute(route);
        }}
      />

      <EditRouteDialog
        target={editRouteTarget}
        value={{
          roadName: editRouteRoadName,
          minutes: editRouteMinutes,
          capacity: editRouteCapacity,
          weightCapacity: editRouteWeightCapacity,
          speedLimitMin: editRouteSpeedLimitMin,
          speedLimitMax: editRouteSpeedLimitMax,
          lanes: editRouteLanes,
          condition: editRouteCondition,
          suggestions: editRouteSuggestions,
        }}
        onChange={(next) => {
          setEditRouteRoadName(next.roadName);
          setEditRouteMinutes(next.minutes);
          setEditRouteCapacity(next.capacity);
          setEditRouteWeightCapacity(next.weightCapacity);
          setEditRouteSpeedLimitMin(next.speedLimitMin);
          setEditRouteSpeedLimitMax(next.speedLimitMax);
          setEditRouteLanes(next.lanes);
          setEditRouteCondition(next.condition);
          setEditRouteSuggestions(next.suggestions);
        }}
        saving={savingRouteEdit}
        onClose={() => {
          setEditRouteTarget(null);
        }}
        onSave={() => {
          void saveRouteEdit();
        }}
      />

      <RoadWidthDialog
        open={widthEditTarget !== null}
        value={widthEditValue}
        onChange={setWidthEditValue}
        onClose={() => {
          setWidthEditTarget(null);
        }}
        onSave={applyWidthEdit}
      />

      <SaveRoadDialog
        open={roadTo !== null}
        fromName={drawingRoadFrom?.name ?? ''}
        toName={roadTo?.name ?? ''}
        distanceLabel={formatDistance(pathDistanceMeters(draftVertices))}
        value={{
          roadName: roadRoadName,
          returnRoadName: roadReturnRoadName,
          sameNameBothWays: roadSameNameBothWays,
          minutes: roadMinutes,
          capacity: roadCapacity,
          weightCapacity: roadWeightCapacity,
          speedLimitMin: roadSpeedLimitMin,
          speedLimitMax: roadSpeedLimitMax,
          lanes: roadLanes,
          condition: roadCondition,
          suggestions: roadSuggestions,
          twoWay: roadTwoWay,
        }}
        onChange={(next) => {
          setRoadRoadName(next.roadName);
          setRoadReturnRoadName(next.returnRoadName);
          setRoadSameNameBothWays(next.sameNameBothWays);
          setRoadMinutes(next.minutes);
          setRoadCapacity(next.capacity);
          setRoadWeightCapacity(next.weightCapacity);
          setRoadSpeedLimitMin(next.speedLimitMin);
          setRoadSpeedLimitMax(next.speedLimitMax);
          setRoadLanes(next.lanes);
          setRoadCondition(next.condition);
          setRoadSuggestions(next.suggestions);
          setRoadTwoWay(next.twoWay);
        }}
        onClose={() => {
          setRoadTo(null);
          setDraftVertices((prev) => prev.slice(0, -1));
          setRoadVertexWidths((prev) => prev.slice(0, -1));
        }}
        onSave={queueRoad}
      />
    </Box>
  );
};

export const RoadsImporterView = (): JSX.Element => (
  <CustomMuiThemeProvider>
    <RoadsImporterContent />
  </CustomMuiThemeProvider>
);
